import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { logError } from '../../lib/logger';
import {
  ChevronLeft, ChevronRight, Search, Cpu, Upload, Loader2, X,
  Calendar as CalIcon, Eye, EyeOff, CheckCircle2, Paintbrush, User, Users,
  Trash2, Save, Layout, BarChart3, ChevronDown, ChevronUp, Building2, Bell, Plus, Clock, Zap,
  Play, Sliders, RotateCcw, AlertTriangle, Trophy, TrendingUp, ArrowRightLeft, Sparkles,
  Star, Edit2, RefreshCw, History
} from 'lucide-react';
import { Input } from '../ui/input';
import {
  format, addDays, addWeeks, subWeeks, addMonths, subMonths, startOfWeek, endOfWeek,
  startOfDay, endOfDay, startOfMonth, endOfMonth, isSameDay, setHours, setMinutes, parseISO
} from 'date-fns';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn, recurrenceToRRule } from '../../lib/utils';
import api from '../../api';
import { TimeGrid, MonthGrid, MiniCalendar, AgendaView, getColor } from '../calendar/CalendarGrid';
import EventModal from '../calendar/EventModal';

import { supabase } from '../../lib/supabase';
import { rbac, validateEventPayload, canEditEvent } from '../../lib/rbac';
import useCalendarStore from '../../store/useCalendarStore';
import SchedulerSidebar from '../scheduler/SchedulerSidebar';
import { useEscapeHierarchy } from '../../hooks/useKeyboardShortcuts';

const VIEWS = [
  { id: 'day', label: 'Day', key: 'd' },
  { id: 'week', label: 'Week', key: 'w' },
  { id: 'month', label: 'Month', key: 'm' },
  { id: 'agenda', label: 'Agenda', key: 'a' },
];

function SidebarSection({ title, icon: Icon, defaultOpen = true, action, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-1">
      <div
        className="flex items-center justify-between px-1 py-1.5 rounded hover:bg-[var(--sidebar-accent)] cursor-pointer group transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="text-[0.6rem] uppercase tracking-widest font-semibold flex flex-1 items-center gap-1.5" style={{ color: 'var(--sidebar-muted)' }}>
          {Icon && <Icon className="w-3 h-3" />}
          {title}
        </div>
        <div className="flex items-center gap-1.5">
          {action && (
            <div onClick={e => e.stopPropagation()}>{action}</div>
          )}
          {open ? (
            <ChevronDown className="w-3 h-3 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
          ) : (
            <ChevronRight className="w-3 h-3 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
          )}
        </div>
      </div>
      {open && <div className="mt-1 space-y-0.5">{children}</div>}
    </div>
  );
}

/* ─── Templates Sidebar Section ─────────────────────────── */
function TemplatesSection({ faculty, batches, currentDate }) {
  const setDraggedTemplate = useCalendarStore(s => s.setDraggedTemplate);
  const fetchEvents = useCalendarStore(s => s.fetchEvents);

  const [templateSearch, setTemplateSearch] = useState('');
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const templateRefreshTrigger = useCalendarStore(s => s.templateRefreshTrigger);

  const fetchAllTemplates = useCallback(async () => {
    setLoading(true);
    try {
      // session_id='all' bypasses session filtering in the backend
      const res = await api.get('/api/templates', { params: { q: templateSearch, session_id: 'all' } });
      setAllTemplates(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [templateSearch]);

  useEffect(() => {
    fetchAllTemplates();
  }, [fetchAllTemplates, templateRefreshTrigger]);

  const handleApplyTemplate = async (templateId) => {
    // We still need a session_id to apply a template to the sandbox, 
    // but the user's request implies they want a general list.
    // If they apply it without a session, we might need to create one or use a default.
    // For now, I'll keep the logic if a session exists, otherwise maybe error?
    // Actually, "dragging and dropping" is the primary interaction requested.
    // Direct "Apply" button might be secondary.
    toast.info('Drag and drop the template onto the calendar grid to stage it.');
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await api.delete(`/api/templates/${templateId}`);
      setAllTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (err) { 
        logError(err, { componentName: 'CalendarPanel', metadata: { action: 'delete_template', templateId } }); 
    }
  };

  const handleUpdateTemplate = async (templateId, updates) => {
    try {
      await api.put(`/api/templates/${templateId}`, updates);
      setAllTemplates(prev => prev.map(t => t.id === templateId ? { ...t, ...updates } : t));
    } catch (err) {
      toast.error('Failed to update template');
    }
  };

  const TemplateCard = ({ t }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [newName, setNewName] = useState(t.name);

    return (
      <div
        key={t.id}
        draggable="true"
        onDragStart={(e) => {
          e.dataTransfer.setData('application/json', JSON.stringify({ type: 'template', ...t }));
          const img = new Image();
          img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
          e.dataTransfer.setDragImage(img, 0, 0);
          setDraggedTemplate(t);
        }}
        onDragEnd={() => setDraggedTemplate(null)}
        className="w-full text-left px-2 py-2 rounded-md text-xs border border-transparent bg-[var(--sidebar-accent)]/50 hover:bg-[var(--sidebar-accent)] hover:border-violet-500/30 transition-all cursor-grab active:cursor-grabbing group relative"
        style={{ color: 'var(--sidebar-fg)' }}
      >
        <div className="font-medium flex items-start justify-between gap-1">
          {isEditing ? (
            <input 
              value={newName} 
              onChange={e => setNewName(e.target.value)}
              onBlur={() => { setIsEditing(false); if (newName !== t.name) handleUpdateTemplate(t.id, { name: newName }); }}
              onKeyDown={e => { 
                if (e.key === 'Enter') { setIsEditing(false); if (newName !== t.name) handleUpdateTemplate(t.id, { name: newName }); }
                if (e.key === 'Escape') { setIsEditing(false); setNewName(t.name); }
              }}
              className="bg-zinc-100 dark:bg-zinc-800 border-none px-1 rounded w-full outline-none text-[0.65rem]"
              autoFocus
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <span className="flex-1 break-words leading-tight" onDoubleClick={() => setIsEditing(true)}>{t.name}</span>
          )}
          <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <button className={cn("p-1 transition-colors", t.is_starred ? "text-amber-500" : "text-zinc-400 hover:text-amber-500")} onClick={(e) => { e.stopPropagation(); handleUpdateTemplate(t.id, { is_starred: !t.is_starred }); }} title="Star Pattern">
              <Star className={cn("w-2.5 h-2.5", t.is_starred && "fill-current")} />
            </button>
            <button className="p-1 text-zinc-400 hover:text-blue-500" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }} title="Rename"><Edit2 className="w-2.5 h-2.5" /></button>
            <button className="p-1 text-zinc-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id); }} title="Delete"><Trash2 className="w-2.5 h-2.5" /></button>
          </div>
        </div>
        {t.description && <p className="text-[0.5rem] mt-1 break-words opacity-50 leading-relaxed font-mono">{t.description}</p>}
        <div className="flex items-center gap-2 mt-1.5 opacity-60">
          <Badge className="text-[0.45rem] px-1 py-0 bg-violet-500/10 text-violet-600 border-none">
            {Array.isArray(t.events) ? t.events.length : 0} Events
          </Badge>
          {t.session_id && <span className="text-[0.45rem] font-bold text-blue-500 uppercase tracking-tighter">Session</span>}
          {t.is_starred && <span className="text-[0.45rem] font-bold text-amber-600 uppercase tracking-tighter">Gold</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Template search */}
      <div className="flex gap-1">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
          <input type="text" placeholder="Search templates..."
            value={templateSearch} onChange={(e) => setTemplateSearch(e.target.value)}
            className="w-full h-8 pl-7 pr-2 rounded-md border border-[var(--sidebar-border)] bg-[var(--sidebar-input)] text-[0.65rem] placeholder:opacity-50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
            style={{ color: 'var(--sidebar-fg)' }}
          />
        </div>
        <button 
          onClick={fetchAllTemplates} 
          disabled={loading}
          className="w-8 h-8 rounded-md bg-violet-500/10 hover:bg-violet-500/20 flex items-center justify-center text-violet-600 dark:text-violet-300 transition-colors"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
        </button>
      </div>

      {/* Templates List */}
      <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
        {allTemplates.length === 0 && !loading && (
          <div className="text-[0.6rem] text-zinc-500 text-center py-8 border-2 border-dashed border-[var(--sidebar-border)] rounded-lg">
            No templates found.
          </div>
        )}
        {allTemplates.map(t => <TemplateCard key={t.id} t={t} />)}
      </div>
      
      <div className="pt-2 text-[0.55rem] text-zinc-500 italic px-1">
        Tip: Drag a template onto a day in the calendar to stage changes.
      </div>
    </div>
  );
}

/* ─── Algorithm Generator Sidebar Section ───────────────── */
const ALGO_LABELS = {
  cpsat: { name: 'CP-SAT', color: '#3B82F6' },
  ga:    { name: 'Genetic', color: '#8B5CF6' },
  sa:    { name: 'Annealing', color: '#F59E0B' },
};

function AlgorithmSection({ setVisibleCalendars }) {
  const settings = useCalendarStore(s => s.settings);
  const batches = useCalendarStore(s => s.batches);
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedBatches, setSelectedBatches] = useState(new Set());

  const STEPS = [
    { id: 'prepare', label: 'PREPARING DATA' },
    { id: 'constraints', label: 'ANALYZING CONSTRAINTS' },
    { id: 'optimize', label: 'RUNNING CP-SAT SOLVER' },
    { id: 'finalize', label: 'SAVING TEMPLATES' },
  ];

  // Initialize selectedBatches when batches load
  useEffect(() => {
    if (batches.length > 0 && selectedBatches.size === 0) {
      setSelectedBatches(new Set(batches.map(b => b.id)));
    }
  }, [batches]);

  const [params, setParams] = useState({
    scheduler_preference_weight: 5,
    scheduler_morning_weight: 3,
    scheduler_compactness_weight: -2,
  });

  useEffect(() => {
    if (!settings) return;
    setParams(p => ({
      ...p,
      scheduler_preference_weight: settings.scheduler_preference_weight ?? p.scheduler_preference_weight,
      scheduler_morning_weight: settings.scheduler_morning_weight ?? p.scheduler_morning_weight,
      scheduler_compactness_weight: settings.scheduler_compactness_weight ?? p.scheduler_compactness_weight,
    }));
  }, [settings]);

  const triggerTemplateRefresh = useCalendarStore(s => s.triggerTemplateRefresh);

  const runComparison = async () => {
    setLoading(true);
    setComparison(null);
    setActiveStep('prepare');

    const payload = { 
      dates: [targetDate],
      morning_preference: params.scheduler_morning_weight,
      compactness: params.scheduler_compactness_weight,
      faculty_preference: params.scheduler_preference_weight,
      batch_ids: selectedBatches.size === batches.length ? null : Array.from(selectedBatches),
      solver_timeout_seconds: 30,
      sa_cooling_rate: 0.995,
    };

    const progressTimer = setInterval(() => {
      setActiveStep(prev => {
        if (prev === 'prepare') return 'constraints';
        if (prev === 'constraints') return 'optimize';
        return prev;
      });
    }, 2000);
    
    try {
      const res = await api.post('/api/ai/schedule/run-optimization', payload, { timeout: 120000 });
      clearInterval(progressTimer);
      
      if (res.data?.error) {
        throw new Error(`Algorithm Engine Error: ${res.data.error}`);
      }

      if (!res.data || !res.data.summary) {
        throw new Error('System created a bad response: Summary data is missing.');
      }

      setActiveStep('finalize');
      await new Promise(r => setTimeout(r, 800));

      setComparison(res.data.summary);
      setVisibleCalendars(new Set(['sandbox']));
      
      triggerTemplateRefresh();
      toast.success('Optimization Complete. All variants saved to templates.');
    } catch (err) {
      clearInterval(progressTimer);
      console.error('[CalendarPanel:AlgorithmSection] Error:', err);
      toast.error(err.message || err.response?.data?.detail || 'Optimization failed'); 
    } finally {
      setLoading(false);
      setActiveStep(null);
    }
  };

  const saveAsTemplate = async (algo, metrics) => {
    try {
      const payload = {
        name: `Optimized ${ALGO_LABELS[algo]?.name || algo} — ${targetDate}`,
        events: metrics.events || [],
        scope: { date: targetDate },
        is_starred: true
      };
      await api.post('/api/templates/promote', payload);
      toast.success('Saved as reusable template');
      fetchAllTemplates(); // Refresh the list immediately
    } catch (err) {
      toast.error('Failed to save template');
    }
  };

  const previewOnCalendar = async (algo, metrics) => {
    try {
      await api.post('/api/pipeline/preview-sandbox', {
        date: targetDate,
        events: metrics.events || []
      });
      toast.success('Solution loaded into Sandbox');
      setVisibleCalendars(new Set(['sandbox'])); // Auto-switch to sandbox view
    } catch (err) {
      toast.error('Failed to load preview');
    }
  };



  const SLIDERS = [
    { key: 'scheduler_preference_weight', label: 'Faculty Preference', min: 0, max: 20, step: 1, unit: '' },
    { key: 'scheduler_morning_weight', label: 'Morning Preference', min: 0, max: 10, step: 1, unit: '' },
    { key: 'scheduler_compactness_weight', label: 'Compactness', min: -20, max: 0, step: 1, unit: '' },
  ];

  return (
    <div className="space-y-3">
      {/* Date + Run */}
      <div className="flex items-center gap-1">
        <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
          className="flex-1 h-7 rounded-md border border-[var(--sidebar-border)] bg-[var(--sidebar-input)] text-[0.6rem] px-2 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
          style={{ color: 'var(--sidebar-fg)' }}
        />
        <button onClick={runComparison} disabled={loading}
          className="w-7 h-7 rounded-md bg-blue-500/10 hover:bg-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-300 disabled:opacity-40 transition-colors"
          title="Run Engine"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
        </button>
      </div>

      {loading && activeStep && (
        <div className="p-3 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 space-y-1 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="text-[0.5rem] uppercase tracking-widest font-bold text-blue-500 mb-2 flex items-center justify-between">
            <span>Optimization Status</span>
            <span className="animate-pulse">Active</span>
          </div>
          {STEPS.map((s, idx) => {
            const stepIdx = STEPS.findIndex(x => x.id === activeStep);
            let status = 'pending';
            if (idx < stepIdx) status = 'done';
            else if (idx === stepIdx) status = 'loading';
            
            return (
              <div key={s.id} className="flex items-center gap-2 text-[0.6rem] py-0.5">
                {status === 'pending' && <div className="w-3 h-3 rounded-full border border-zinc-200 dark:border-zinc-800" />}
                {status === 'loading' && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
                {status === 'done' && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                <span className={cn(
                  status === 'pending' && "text-zinc-400",
                  status === 'loading' && "text-blue-500 font-bold",
                  status === 'done' && "text-zinc-500"
                )}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Advanced Settings Toggle */}
      <button 
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1.5 text-[0.6rem] font-bold uppercase tracking-widest text-zinc-500 hover:text-blue-500 transition-colors pt-1 group w-full"
      >
        <Sliders className="w-3 h-3 group-hover:rotate-12 transition-transform" />
        Advanced Settings
        {showAdvanced ? <ChevronUp className="w-3 h-3 ml-auto opacity-40" /> : <ChevronDown className="w-3 h-3 ml-auto opacity-40" />}
      </button>

      {showAdvanced && (
        <div className="space-y-4 p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/10 animate-in fade-in slide-in-from-top-1 duration-200">
          {/* Batch Selector */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-[0.55rem] uppercase tracking-wide font-semibold opacity-60 flex items-center gap-1.5" style={{ color: 'var(--sidebar-fg)' }}>
                <Users className="w-2.5 h-2.5" />
                Select Batches
              </span>
              <button 
                onClick={() => {
                  if (selectedBatches.size === batches.length) setSelectedBatches(new Set());
                  else setSelectedBatches(new Set(batches.map(b => b.id)));
                }}
                className="text-[0.55rem] font-bold text-blue-500 hover:underline"
              >
                {selectedBatches.size === batches.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1.5 pr-1.5 custom-scrollbar">
              {batches.map(b => (
                <label key={b.id} className="flex items-center gap-2 cursor-pointer group">
                  <input 
                    type="checkbox" 
                    checked={selectedBatches.has(b.id)}
                    onChange={() => {
                      const next = new Set(selectedBatches);
                      if (next.has(b.id)) next.delete(b.id);
                      else next.add(b.id);
                      setSelectedBatches(next);
                    }}
                    className="w-3 h-3 rounded border-zinc-300 dark:border-zinc-700 text-blue-600 focus:ring-blue-500 bg-white dark:bg-zinc-800 transition-colors cursor-pointer"
                  />
                  <span className="text-[0.6rem] truncate transition-colors group-hover:text-blue-500" style={{ color: 'var(--sidebar-fg)' }}>{b.name}</span>
                </label>
              ))}
              {batches.length === 0 && <div className="text-[0.55rem] text-zinc-500 text-center py-1">No batches found</div>}
            </div>
          </div>

          {/* Param sliders */}
          <div className="space-y-3 pt-2 border-t border-blue-500/10">
            {SLIDERS.map(s => (
              <div key={s.key}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[0.55rem] uppercase tracking-wide font-semibold opacity-60" style={{ color: 'var(--sidebar-fg)' }}>{s.label}</span>
                  <span className="text-[0.6rem] font-bold text-blue-500 tabular-nums">{params[s.key]}{s.unit}</span>
                </div>
                <input type="range" min={s.min} max={s.max} step={s.step} value={params[s.key]}
                  onChange={(e) => setParams(p => ({ ...p, [s.key]: s.step === 0.001 ? parseFloat(e.target.value) : Number(e.target.value) }))}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-zinc-200 dark:bg-zinc-700 accent-blue-600"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {comparison?.comparison_matrix && (
        <div className="space-y-1.5 pt-1 border-t border-[var(--sidebar-border)]">
          <div className="text-[0.55rem] uppercase tracking-widest text-zinc-400 font-semibold">Results</div>
          {comparison.recommendation && (
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/20">
              <Trophy className="w-3 h-3 text-amber-500 shrink-0" />
              <span className="text-[0.6rem] font-bold text-amber-600 dark:text-amber-400">{ALGO_LABELS[comparison.recommendation]?.name || comparison.recommendation}</span>
              <span className="text-[0.55rem] text-zinc-400 ml-auto">Best</span>
            </div>
          )}
          {Object.entries(comparison.comparison_matrix).map(([algo, metrics]) => (
            <div key={algo} className="flex items-center gap-2 px-1 flex-wrap">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: ALGO_LABELS[algo]?.color || '#888' }} />
                <span className="text-[0.55rem] break-words" style={{ color: 'var(--sidebar-fg)' }}>{ALGO_LABELS[algo]?.name || algo}</span>
              </div>
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-[0.6rem] font-bold tabular-nums" style={{ color: ALGO_LABELS[algo]?.color }}>{metrics.grade}</span>
                <div className="flex items-center gap-0.5">
                  <button 
                    onClick={() => previewOnCalendar(algo, metrics)}
                    className="p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-400 hover:text-indigo-500 transition-colors"
                    title="Preview on Calendar"
                  >
                    <Eye className="w-2.5 h-2.5" />
                  </button>
                  <button 
                    onClick={() => saveAsTemplate(algo, metrics)}
                    className="p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-400 hover:text-blue-500 transition-colors"
                    title="Save as Template"
                  >
                    <Save className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {comparison?.status === 'no_data' && (
        <div className="text-[0.6rem] text-zinc-500 text-center py-2">No source data for this date</div>
      )}
    </div>
  );
}

export default function CalendarPanel() {
  // ─── Persistent state (survives route changes via Zustand) ───
  const view = useCalendarStore(s => s.view);
  const setView = useCalendarStore(s => s.setView);
  const currentDate = useCalendarStore(s => s.currentDate);
  const setCurrentDate = useCalendarStore(s => s.setCurrentDate);
  const events = useCalendarStore(s => s.events);
  const setEvents = useCalendarStore(s => s.setEvents);
  const calendarsStore = useCalendarStore(s => s.calendars);
  const setCalendars = useCalendarStore(s => s.setCalendars);
  const visibleCalendars = useCalendarStore(s => s.visibleCalendars);
  const setVisibleCalendars = useCalendarStore(s => s.setVisibleCalendars);
  const faculty = useCalendarStore(s => s.faculty);
  const setFaculty = useCalendarStore(s => s.setFaculty);
  const batches = useCalendarStore(s => s.batches);
  const setBatches = useCalendarStore(s => s.setBatches);
  const subjects = useCalendarStore(s => s.subjects);
  const setSubjects = useCalendarStore(s => s.setSubjects);
  const branches = useCalendarStore(s => s.branches);
  const setBranches = useCalendarStore(s => s.setBranches);
  const assignments = useCalendarStore(s => s.assignments);
  const setAssignments = useCalendarStore(s => s.setAssignments);
  const settings = useCalendarStore(s => s.settings);
  const setSettings = useCalendarStore(s => s.setSettings);
  const hasAlgoEvents = useCalendarStore(s => s.hasAlgoEvents);
  const setHasAlgoEvents = useCalendarStore(s => s.setHasAlgoEvents);
  const sidebarOpen = useCalendarStore(s => s.sidebarOpen);
  const setSidebarOpen = useCalendarStore(s => s.setSidebarOpen);
  const showAvailFor = useCalendarStore(s => s.showAvailFor);
  const setShowAvailFor = useCalendarStore(s => s.setShowAvailFor);
  const painterMode = useCalendarStore(s => s.painterMode);
  const setPainterMode = useCalendarStore(s => s.setPainterMode);
  const painterForm = useCalendarStore(s => s.painterForm);
  const setPainterForm = useCalendarStore(s => s.setPainterForm);
  const searchOpen = useCalendarStore(s => s.searchOpen);
  const setSearchOpen = useCalendarStore(s => s.setSearchOpen);
  const searchType = useCalendarStore(s => s.searchType);
  const setSearchType = useCalendarStore(s => s.setSearchType);
  const searchEntityId = useCalendarStore(s => s.searchEntityId);
  const setSearchEntityId = useCalendarStore(s => s.setSearchEntityId);
  const highlights = useCalendarStore(s => s.highlights);

  // Scheduler sidebar
  const schedulerSidebarOpen = useCalendarStore(s => s.schedulerSidebarOpen);
  const setSchedulerSidebarOpen = useCalendarStore(s => s.setSchedulerSidebarOpen);
  const activeJobId = useCalendarStore(s => s.activeJobId);

  // Tentative preview state
  const tentativeSessionId = useCalendarStore(s => s.tentativeSessionId);
  const tentativeMetrics = useCalendarStore(s => s.tentativeMetrics);
  const tentativeDate = useCalendarStore(s => s.tentativeDate);
  const setTentativeSession = useCalendarStore(s => s.setTentativeSession);
  const clearTentativeSession = useCalendarStore(s => s.clearTentativeSession);
  const setHighlights = useCalendarStore(s => s.setHighlights);

  // Staged Templates (Generative Studio Drag & Drop Buffer)
  const stagedTemplates = useCalendarStore(s => s.stagedTemplates);
  const setStagedTemplate = useCalendarStore(s => s.setStagedTemplate);
  const removeStagedTemplate = useCalendarStore(s => s.removeStagedTemplate);
  const clearStagedTemplates = useCalendarStore(s => s.clearStagedTemplates);

  // ─── Sidebar Resize Logic ───────────────────────────────
  const [sidebarWidth, setSidebarWidth] = useState(280); // Default to 280px (the size requested)
  const [isResizing, setIsResizing] = useState(false);

  const startResizing = useCallback((e) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback((e) => {
    if (isResizing) {
      const newWidth = e.clientX;
      if (newWidth >= 280 && newWidth <= 480) {
        setSidebarWidth(newWidth);
      }
    }
  }, [isResizing]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
    } else {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    }
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  // ─── Current admin profile (persisted in localStorage at login) ───
  const profile = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('admin_profile') || '{}'); }
    catch { return {}; }
  }, []);

  const username = profile.admin_id || '';

  // ─── Ephemeral state (resets on unmount, that's ok) ───
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [initModalOpen, setInitModalOpen] = useState(false);
  const [initLockHours, setInitLockHours] = useState(24);
  const [initRollingWeeks, setInitRollingWeeks] = useState(4);
  const [initDraftWeeks, setInitDraftWeeks] = useState(2);
  const [initStartDate, setInitStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const [editEvent, setEditEvent] = useState(null);
  const [selectedDayForModal, setSelectedDayForModal] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [viewDropOpen, setViewDropOpen] = useState(false);
  const [painterDropOpen, setPainterDropOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateSaving, setSaveTemplateSaving] = useState(false);
  const [updatingSchedule, setUpdatingSchedule] = useState(false);
  const [conflictEventIds, setConflictEventIds] = useState(new Set());
  const [lockedConflictIds, setLockedConflictIds] = useState(new Set());
  const [processingChanges, setProcessingChanges] = useState(false);
  const [unlockingLocked, setUnlockingLocked] = useState(false);
  const [lastRepairStats, setLastRepairStats] = useState(null);
  const [hideDraftEvents, setHideDraftEvents] = useState(false);

  // Scheduling Engine Integration States
  const [isInitialized, setIsInitialized] = useState(false);
  const [sigChanged, setSigChanged] = useState(false);
  const [eventCount, setEventCount] = useState(0);
  // buttonState: 'idle' | 'preflight' | 'running' | 'completing'
  const [buttonState, setButtonState] = useState('idle');
  const [lastRunAt, setLastRunAt] = useState(null);   // ISO string from API
  const [advancingWindow, setAdvancingWindow] = useState(false);
  const completingTimerRef = useRef(null);

  // Drift = how many calendar days behind the last sync is vs. today
  const driftDays = useMemo(() => {
    if (!lastRunAt || !isInitialized) return 0;
    const lastDate = new Date(lastRunAt);
    lastDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((today - lastDate) / 86400000));
  }, [lastRunAt, isInitialized]);

  const fetchSchedulingStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/scheduling/status');
      setIsInitialized(res.data.initialized);
      setSigChanged(res.data.sig_changed || false);
      setEventCount(res.data.event_count || 0);
      if (res.data.last_run_at) setLastRunAt(res.data.last_run_at);

      // Rehydrate active job on page reload so progress bar shows immediately
      if (res.data.active_job_id && res.data.active_job_status === 'running') {
        const store = useCalendarStore.getState();
        store.setActiveJobId(res.data.active_job_id);
        store.updateSchedulingJob({
          id: res.data.active_job_id,
          status: 'running',
          events_added: res.data.active_job_events_added || 0,
          type: 'initialize',
          started_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
        });
        setButtonState('running');
      }
    } catch (err) {
      console.error('Failed to fetch scheduling status', err);
    }
  }, []);

  // ── Version history modal ────────────────────────────────────────────
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);
  
  // Selection for diffing
  const [selectedDiffA, setSelectedDiffA] = useState(null);
  const [selectedDiffB, setSelectedDiffB] = useState(null);
  const [diffResult, setDiffResult] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  // Label inline editing
  const [editingVersionId, setEditingVersionId] = useState(null);
  const [editVersionLabel, setEditVersionLabel] = useState('');

  // Restore confirmation
  const [restoreVersionTarget, setRestoreVersionTarget] = useState(null);
  const [restoreVersionLoading, setRestoreVersionLoading] = useState(false);

  // Delete loading
  const [deletingVersionId, setDeletingVersionId] = useState(null);

  const fetchVersionHistory = useCallback(async () => {
    setVersionHistoryLoading(true);
    try {
      const res = await api.get('/api/scheduling/versions');
      setVersionHistory(res.data.versions || []);
    } catch (err) {
      console.error('Failed to fetch version history', err);
      toast.error('Failed to load version history');
    } finally {
      setVersionHistoryLoading(false);
    }
  }, []);

  const handleSaveVersionLabel = async (id) => {
    if (!editVersionLabel.trim()) return;
    try {
      await api.patch(`/api/scheduling/versions/${id}`, { label: editVersionLabel });
      setVersionHistory(v => v.map(ver => ver.id === id ? { ...ver, label: editVersionLabel } : ver));
      toast.success('Version label updated successfully');
      setEditingVersionId(null);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to update label');
    }
  };

  const handleDeleteVersion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this version snapshot? This will not affect the live calendar.')) return;
    setDeletingVersionId(id);
    try {
      await api.delete(`/api/scheduling/versions/${id}`);
      setVersionHistory(v => v.filter(ver => ver.id !== id));
      if (selectedDiffA === id) { setSelectedDiffA(null); setDiffResult(null); }
      if (selectedDiffB === id) { setSelectedDiffB(null); setDiffResult(null); }
      toast.success('Version deleted successfully');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to delete version');
    } finally {
      setDeletingVersionId(null);
    }
  };

  const handleRestoreVersion = async () => {
    if (!restoreVersionTarget) return;
    setRestoreVersionLoading(true);
    try {
      const res = await api.post(`/api/scheduling/versions/${restoreVersionTarget.id}/restore`);
      toast.success(`Successfully restored to v${restoreVersionTarget.version_num}! ${res.data.inserted_events} events applied.`);
      setRestoreVersionTarget(null);
      setIsVersionModalOpen(false);
      // Re-fetch calendar events and scheduling status
      fetchEvents();
      fetchSchedulingStatus();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to restore version');
    } finally {
      setRestoreVersionLoading(false);
    }
  };

  const handleRunDiff = useCallback(async (a, b) => {
    if (!a || !b || a === b) return;
    setDiffLoading(true);
    setDiffResult(null);
    try {
      const res = await api.get(`/api/scheduling/versions/diff?a=${a}&b=${b}`);
      setDiffResult(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to generate diff');
    } finally {
      setDiffLoading(false);
    }
  }, []);

  const selectForDiff = (id) => {
    if (!selectedDiffA) {
      setSelectedDiffA(id);
      setDiffResult(null);
    } else if (!selectedDiffB && id !== selectedDiffA) {
      setSelectedDiffB(id);
      handleRunDiff(selectedDiffA, id);
    } else {
      setSelectedDiffA(id);
      setSelectedDiffB(null);
      setDiffResult(null);
    }
  };

  const clearDiff = () => {
    setSelectedDiffA(null);
    setSelectedDiffB(null);
    setDiffResult(null);
  };


  const JOB_TYPE_LABEL = {
    'initialize':       { label: 'Init',    color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' },
    'update':           { label: 'Update',  color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
    'advance-window':   { label: 'Advance', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
    'restore-snapshot': { label: 'Restore', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  };

  const handleInitializeOrUpdate = async (overrides = null) => {
    // Guard: silently reject if already in flight (button is disabled, but belt-and-suspenders)
    const store = useCalendarStore.getState();
    if (store.activeJobId || buttonState !== 'idle') return;

    // ── Phase 1: Pre-flight validation ──────────────────────────────────
    setButtonState('preflight');
    let preflightOk = false;
    try {
      const pf = await api.post('/api/scheduling/preflight', {});

      if (pf.data?.status === 'no_changes') {
        toast.info(pf.data.message || 'No configuration or availability changes detected — schedule is already up to date.');
        setButtonState('idle');
        return;
      }

      if (pf.data?.status === 'error') {
        toast.error(pf.data.message || 'Pre-flight check failed. Please review your configuration.', { duration: 8000 });
        setButtonState('idle');
        return;
      }

      // Show any non-blocking warnings
      (pf.data?.warnings || []).forEach(w => toast.warning(w, { duration: 6000 }));
      preflightOk = true;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Pre-flight check failed. Server may be unreachable.');
      setButtonState('idle');
      return;
    }

    if (!preflightOk) return;

    // ── Phase 2: Launch the background job ──────────────────────────────
    try {
      const res = await api.post('/api/scheduling/update', overrides || {});

      if (res.data?.status === 'no_changes') {
        // Defensive: preflight may have missed a race; handle gracefully
        toast.info(res.data.message || 'Schedule is already up to date.');
        setButtonState('idle');
        return;
      }

      if (res.data?.status === 'started' && res.data?.job_id) {
        setIsInitialized(true);
        setButtonState('running');
        const optimJob = {
          id: res.data.job_id,
          status: 'running',
          type: isInitialized ? 'update' : 'initialize',
          events_added: 0,
          started_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
        };
        store.setActiveJobId(res.data.job_id);
        store.updateSchedulingJob(optimJob);
        // Do not toast — button itself is now the live progress indicator
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.warning('A scheduling job is already running. Cancel it first, or wait for it to finish.');
      } else {
        toast.error(err.response?.data?.detail || err.message || 'Failed to start scheduling job.');
      }
      setButtonState('idle');
      fetchSchedulingStatus();
    }
  };

  const handleInitializeOrUpdateClick = () => {
    if (!isInitialized) {
      setInitLockHours(settings.schedule_lock_hours ?? 24);
      setInitRollingWeeks(settings.rolling_window_weeks ?? 4);
      setInitDraftWeeks(settings.draft_resolution_weeks ?? 2);
      setInitStartDate(settings.allotment_start_date ?? format(new Date(), 'yyyy-MM-dd'));
      setInitModalOpen(true);
    } else {
      handleInitializeOrUpdate();
    }
  };

  const handleConfirmInitialization = async () => {
    setInitModalOpen(false);
    try {
      // 1. Save to backend settings
      await api.put('/api/team', {
        settings: {
          schedule_lock_hours: initLockHours,
          rolling_window_weeks: initRollingWeeks,
          draft_resolution_weeks: initDraftWeeks,
          allotment_start_date: initStartDate
        }
      });
      // 2. Sync local Zustand store
      setSettings({
        ...settings,
        schedule_lock_hours: initLockHours,
        rolling_window_weeks: initRollingWeeks,
        draft_resolution_weeks: initDraftWeeks,
        allotment_start_date: initStartDate
      });
      // 3. Trigger scheduling update/initialize job with overrides
      await handleInitializeOrUpdate({
        schedule_lock_hours: initLockHours,
        rolling_window_weeks: initRollingWeeks,
        draft_resolution_weeks: initDraftWeeks,
        allotment_start_date: initStartDate
      });
      toast.success('Scheduling options updated and engine started.');
    } catch (err) {
      console.error('[CalendarPanel] Failed to save/start initialization overrides:', err);
      toast.error(err.response?.data?.detail || 'Failed to start scheduling engine with custom settings');
    }
  };

  // Phase 1 — dry-run conflict detection + credit-summary refresh
  const handleCheckConflicts = async () => {
    setUpdatingSchedule(true);
    try {
      // Run both in parallel: conflict detection + lightweight credit summary
      const [conflictRes, creditRes] = await Promise.allSettled([
        api.post('/api/scheduling/check-conflicts', {}),
        api.get('/api/scheduling/credit-summary'),
      ]);

      // ── Handle conflict results ──
      if (conflictRes.status === 'fulfilled') {
        const { has_conflicts, conflicts, locked_conflicts, message } = conflictRes.value.data;
        if (!has_conflicts) {
          setConflictEventIds(new Set());
          setLockedConflictIds(new Set());
          setLastRepairStats(null);
          toast.success(message || 'No conflicts detected. Schedule is healthy.');
        } else {
          setConflictEventIds(new Set((conflicts || []).map(String)));
          setLockedConflictIds(new Set((locked_conflicts || []).map(String)));
          const lockedNote = locked_conflicts?.length
            ? ` (${locked_conflicts.length} locked — use Unlock button to make them repairable)`
            : '';
          toast.warning(`${message}${lockedNote}`, { duration: 6000 });
        }
      } else {
        toast.error('Failed to check conflicts');
      }

      // ── Handle credit summary (soft failure ok) ──
      if (creditRes.status === 'fulfilled') {
        const { batches: creditBatches, last_run_at, sig_changed } = creditRes.value.data;

        if (sig_changed) {
          toast.warning(
            'Config has changed since the last engine run. Go to Infrastructure Hub → Sync Config Changes to regenerate the schedule.',
            { duration: 8000 }
          );
        } else if (creditBatches?.length) {
          // Build a compact summary toast
          const lines = [];
          for (const batch of creditBatches) {
            const deficitSubjects = batch.subjects.filter(
              s => s.weekly_required_hours > 0 && s.scheduled_hours < s.weekly_required_hours
            );
            if (deficitSubjects.length > 0) {
              const names = deficitSubjects.map(s => s.subject_name).join(', ');
              lines.push(`${batch.batch_name}: deficit in ${names}`);
            }
          }
          if (lines.length > 0) {
            toast.info(`Hour allocation notice:\n${lines.join('\n')}`, { duration: 7000 });
          }
        }
      }

      fetchEvents();
    } catch (err) {
      console.error('[CalendarPanel] conflict check failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to check conflicts');
    } finally {
      setUpdatingSchedule(false);
    }
  };

  // Phase 2 — incremental repair on detected conflicts
  const handleProcessChanges = async () => {
    setProcessingChanges(true);
    try {
      const ids = Array.from(conflictEventIds);
      const res = await api.post('/api/scheduling/process-changes', { conflict_ids: ids });
      const { placed, deficit, conflicts: total, message } = res.data;
      setLastRepairStats({ placed, deficit, total });
      setConflictEventIds(new Set());
      if (deficit > 0) {
        toast.warning(message || `Placed ${placed}, ${deficit} in deficit.`, { duration: 6000 });
      } else {
        toast.success(message || `All ${placed} lecture(s) successfully rescheduled!`);
      }
      fetchEvents();
    } catch (err) {
      console.error('[CalendarPanel] process-changes failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to process changes');
    } finally {
      setProcessingChanges(false);
    }
  };

  // Unlock locked conflicting events so they become repairable
  const handleUnlockLockedConflicts = async () => {
    if (lockedConflictIds.size === 0) return;
    setUnlockingLocked(true);
    try {
      const ids = Array.from(lockedConflictIds);
      const res = await api.post('/api/scheduling/unlock-conflicts', { event_ids: ids });
      const { unlocked, message } = res.data;
      if (unlocked?.length) {
        // Move newly-unlocked IDs into the repairable conflict set
        setConflictEventIds(prev => new Set([...prev, ...unlocked.map(String)]));
        setLockedConflictIds(new Set());
        toast.success(message || `Unlocked ${unlocked.length} event(s). Click Process Changes to repair.`);
      } else {
        toast.warning(message || 'No events were unlocked.');
      }
    } catch (err) {
      console.error('[CalendarPanel] unlock failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to unlock events');
    } finally {
      setUnlockingLocked(false);
    }
  };

  const handleTriggerUpdate = () => {
    if (conflictEventIds.size > 0) {
      handleProcessChanges();
    } else {
      handleCheckConflicts();
    }
  };

  const handleAdvanceWindow = async () => {
    const store = useCalendarStore.getState();
    // Silently blocked while any job is running (button is already disabled)
    if (store.activeJobId || advancingWindow) return;

    setAdvancingWindow(true);
    try {
      const res = await api.post('/api/scheduling/advance-window');

      if (res.data?.status === 'started' && res.data?.job_id) {
        const optimJob = {
          id: res.data.job_id,
          status: 'running',
          type: 'advance-window',
          events_added: 0,
          started_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
        };
        store.setActiveJobId(res.data.job_id);
        store.updateSchedulingJob(optimJob);
        // Button converts to progress bar automatically via activeJobId
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.warning('A scheduling job is already running. Please wait for it to finish.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to advance schedule window');
      }
      setAdvancingWindow(false);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On mobile, default the sidebar to closed
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setSchedulerSidebarOpen(false);
    }
  }, [isMobile]);

  const toggleAvailVisibility = (key) => {
    setShowAvailFor(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ─── Data Fetching ────────────────────────────────────
  // ─── Data Syncing ───────────────────────────────────────
  
  const calendars = useMemo(() => {
    const baseCals = [
      { id: 'main', name: 'Main Calendar', color: 'blue' },
      { id: 'sandbox', name: 'Sandbox', color: 'amber' },
      { id: 'algorithm', name: 'Draft (Algorithm)', color: 'emerald' }
    ];

    const facultyCals = (faculty || []).map(f => ({ id: `faculty_${f.id}`, name: f.name, type: 'faculty', color: 'indigo' }));
    const batchCals = (batches || []).map(b => ({ id: `batch_${b.id}`, name: b.name, type: 'batch', color: 'rose' }));
    const branchCals = (branches || []).map(br => ({ id: `branch_${br.id}`, name: br.name, type: 'branch', color: 'teal' }));

    return [...baseCals, ...facultyCals, ...batchCals, ...branchCals];
  }, [faculty, batches, branches]);

  // Handle visibility initialization for new entities
  useEffect(() => {
    if (calendars.length > 3) { // more than just base cals
      setVisibleCalendars(prev => {
        const next = new Set(prev);
        let changed = false;
        calendars.forEach(cal => {
          if (!next.has(cal.id)) {
            next.add(cal.id);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [calendars.length]);

  const fetchEvents = useCallback(async () => {
    const { start, end } = getViewRange(view, currentDate);
    try {
      const { data, error } = await supabase
        .from('scheduled_events')
        .select('*')
        .eq('team_id', profile.team_id)
        .gte('start_time', start.toISOString())
        .lte('start_time', end.toISOString());
      
      if (error) throw error;
      setEvents(data || []);
      setHasAlgoEvents((data || []).some(e => (e.calendar_ids || []).includes('algorithm')));
    } catch (err) {
      console.error('[CalendarPanel] fetchEvents error:', err);
    }
  }, [view, currentDate, profile.team_id]);



  const fetchLookups = useCallback(async () => {
    try {
      const [fRes, bRes, brRes, aRes, sRes, subRes] = await Promise.all([
        supabase.from('faculty').select('*').eq('team_id', profile.team_id).order('name'),
        supabase.from('batches').select('*').eq('team_id', profile.team_id).order('name'),
        supabase.from('branches').select('*').eq('team_id', profile.team_id).order('name'),
        supabase.from('faculty_assignments').select('*').eq('team_id', profile.team_id),
        supabase.from('teams').select('settings').eq('id', profile.team_id).maybeSingle(),
        supabase.from('subjects').select('*').eq('team_id', profile.team_id).order('name')
      ]);

      if (fRes.data) setFaculty(fRes.data);
      if (bRes.data) setBatches(bRes.data);
      if (brRes.data) setBranches(brRes.data);
      if (subRes.data) setSubjects(subRes.data);
      if (aRes.data) {
        const flattened = aRes.data.map(a => {
          const fac = fRes.data?.find(f => f.id === a.faculty_id);
          const sub = subRes.data?.find(s => s.id === a.subject_id);
          const bat = bRes.data?.find(b => b.id === a.batch_id);
          return {
            ...a,
            faculty_name: fac?.name,
            subject_name: sub?.name,
            batch_name: bat?.name || bat?.code,
          };
        });
        setAssignments(flattened);
      }
      if (sRes.data && sRes.data.settings) setSettings(sRes.data.settings);
      
      console.log(`[CalendarPanel] Lookups loaded: Faculty(${fRes.data?.length}), Batches(${bRes.data?.length}), Subjects(${subRes.data?.length})`);
    } catch (err) {
      console.error('[CalendarPanel] fetchLookups error:', err);
    }
  }, [profile.team_id]);

  useEffect(() => { fetchLookups(); }, [fetchLookups]);
  useEffect(() => { fetchEvents(); fetchSchedulingStatus(); }, [fetchEvents, fetchSchedulingStatus]);

  // When activeJobId clears (job finished/cancelled/failed): show 100% for 4s then revert
  useEffect(() => {
    if (!activeJobId && buttonState === 'running') {
      setButtonState('completing');
      clearTimeout(completingTimerRef.current);
      completingTimerRef.current = setTimeout(() => {
        setButtonState('idle');
        fetchSchedulingStatus(); // refresh sig/last_run_at after job finishes
      }, 4000);
    }
    // If advancingWindow job finished — just clear it
    if (!activeJobId && advancingWindow) {
      setAdvancingWindow(false);
    }
  }, [activeJobId, buttonState, advancingWindow, fetchSchedulingStatus]);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(completingTimerRef.current), []);

  // Tentative mode: auto-navigate to scheduled date and set sandbox visible
  useEffect(() => {
    if (tentativeSessionId && tentativeDate) {
      setCurrentDate(new Date(tentativeDate));
      setView('day');
      // Show only sandbox calendar
      setVisibleCalendars(new Set(['sandbox']));
    }
  }, [tentativeSessionId]);

  // Current time ticker
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  // ─── Navigation & Unload Locks ────────────────────────
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (Object.keys(useCalendarStore.getState().stagedTemplates || {}).length > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved staged templates. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ─── Keyboard Shortcuts ───
  useEscapeHierarchy([
    { id: 'calendar-modal', active: modalOpen, onEscape: () => { setModalOpen(false); setEditEvent(null); } },
    { id: 'day-events-modal', active: !!selectedDayForModal, onEscape: () => setSelectedDayForModal(null) },
    { id: 'calendar-search', active: searchOpen, onEscape: () => setSearchOpen(false) },
    { id: 'save-template', active: saveTemplateOpen, onEscape: () => setSaveTemplateOpen(false) },
    { id: 'view-drop', active: viewDropOpen, onEscape: () => setViewDropOpen(false) },
    { id: 'painter-drop', active: painterDropOpen, onEscape: () => setPainterDropOpen(false) },
    { id: 'init-modal', active: initModalOpen, onEscape: () => setInitModalOpen(false) },
  ]);

  useEffect(() => {
    const handler = (e) => {
      if (modalOpen || searchOpen || saveTemplateOpen || !!selectedDayForModal || initModalOpen) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      // Don't handle shortcuts if any modifier is pressed (except arrows)
      const key = e.key.toLowerCase();
      switch (key) {
        case 'm': if (!e.shiftKey) setView('month'); break;
        case 'a': if (!e.shiftKey) setView('agenda'); break;
        case 'w': if (!e.shiftKey) setView('week'); break;
        case 'd': if (!e.shiftKey) setView('day'); break;
        case 't': if (!e.shiftKey) setCurrentDate(new Date()); break;
        case 'arrowleft': navigate(-1); break;
        case 'arrowright': navigate(1); break;
        case 'e': if (!e.shiftKey) openNewEvent(); break;
        case '[': setSidebarOpen(!sidebarOpen); break;
        case ']': setSchedulerSidebarOpen(!schedulerSidebarOpen); break;
        default: break;
      }
      // Search: rigidly check e.key
      if (key === '/' && !e.shiftKey) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [modalOpen, searchOpen, saveTemplateOpen, view, currentDate, sidebarOpen, schedulerSidebarOpen]);

  // ─── Navigation ───────────────────────────────────────
  const navigate = useCallback((dir) => {
    setCurrentDate(prev => {
      if (view === 'day') return addDays(prev, dir);
      if (view === 'week') return dir > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1);
      return dir > 0 ? addMonths(prev, 1) : subMonths(prev, 1); // month & agenda
    });
  }, [view]);

  // ─── Event CRUD (Optimistic) ──────────────────────────
  const openNewEvent = async (day, hour) => {
    const d = day || currentDate;
    const h = hour ?? 9;
    const start = setMinutes(setHours(d, h), 0);
    const end = setMinutes(setHours(d, h + 1), 0);

    // Painter mode: directly create availability event (no modal)
    if (painterMode) {
      const tempId = `temp_${Date.now()}`;
      const avEvent = {
        id: tempId,
        event_type: 'availability',
        entity_type: painterMode.entity_type,
        entity_id: painterMode.entity_id,
        availability_mode: painterMode.availability_mode,
        title: `${painterMode.entity_name} - ${painterMode.availability_mode}`,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        color: painterMode.availability_mode === 'preferred' ? 'emerald' : 'rose',
        is_all_day: false,
        calendar_ids: ['main'],
        team_id: profile.team_id,
      };
      // Optimistic add
      setEvents(prev => [...prev, avEvent]);
      // Ensure visibility
      setShowAvailFor(prev => {
        const next = new Set(prev);
        next.add(`${painterMode.entity_type}_${painterMode.entity_id}`);
        return next;
      });
      try {
        // Send create payload without the client-generated tempId
        const { id: _tempId, ...createPayload } = avEvent;
        await api.post('/api/calendar-events', createPayload);
        fetchEvents();
      } catch {
        toast.error('Failed to paint availability block');
        setEvents(prev => prev.filter(e => e.id !== tempId));
      }
      return;
    }

    setEditEvent({
      title: '',
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      color: 'blue',
    });
    setModalOpen(true);
  };

  const openNewEventFromDrag = async (day, startMin, endMin) => {
    const start = setMinutes(setHours(day, Math.floor(startMin / 60)), startMin % 60);
    const end = setMinutes(setHours(day, Math.floor(endMin / 60)), endMin % 60);

    // Painter mode: directly create availability event (no modal)
    if (painterMode) {
      const tempId = `temp_${Date.now()}`;
      const avEvent = {
        id: tempId,
        event_type: 'availability',
        entity_type: painterMode.entity_type,
        entity_id: painterMode.entity_id,
        availability_mode: painterMode.availability_mode,
        title: `${painterMode.entity_name} - ${painterMode.availability_mode}`,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        color: painterMode.availability_mode === 'preferred' ? 'emerald' : 'rose',
        is_all_day: false,
        calendar_ids: ['main'],
        team_id: profile.team_id,
      };
      // Optimistic add
      setEvents(prev => [...prev, avEvent]);
      // Ensure visibility
      setShowAvailFor(prev => {
        const next = new Set(prev);
        next.add(`${painterMode.entity_type}_${painterMode.entity_id}`);
        return next;
      });
      try {
        // Send create payload without the client-generated tempId
        const { id: _tempId, ...createPayload } = avEvent;
        await api.post('/api/calendar-events', createPayload);
        fetchEvents();
      } catch {
        toast.error('Failed to paint availability block');
        setEvents(prev => prev.filter(e => e.id !== tempId));
      }
      return;
    }

    setEditEvent({
      title: '',
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      color: 'blue',
    });
    setModalOpen(true);
  };

  const handleEventClick = (event) => {
    setEditEvent(event);
    setModalOpen(true);
  };

  const handleSaveEvent = async (formData) => {
    setModalOpen(false);
    const isEdit = !!formData.id && !formData.id.toString().startsWith('temp_');

    // Safety Check: RBAC
    if (!rbac.canEditEvents()) {
      toast.error('You do not have permission to edit the schedule');
      return;
    }

    const eventPayload = {
      title: formData.title,
      start_time: formData.start_time,
      end_time: formData.end_time,
      description: formData.description || '',
      color: formData.color || 'blue',
      recurrence_rule: recurrenceToRRule(formData.recurrence),
      recurrence_exceptions: formData.recurrence_exceptions || {},
      room: formData.room || null,
      faculty_id: formData.faculty_id || null,
      batch_id: formData.batch_id || null,
      branch_id: formData.branch_id || null,
      subject_id: formData.subject_id || null,
      event_type: formData.event_type || 'lecture',
      entity_type: formData.entity_type || null,
      entity_id: formData.entity_id || null,
      availability_mode: formData.availability_mode || null,
      is_all_day: formData.is_all_day || false,
      calendar_ids: formData.calendar_ids || [formData.calendar_id || 'main'],
      team_id: profile.team_id
    };

    // Safety Check: Input Validation
    const validation = validateEventPayload(eventPayload);
    if (!validation.valid) {
      toast.error(validation.errors[0]);
      return;
    }

    if (isEdit) {
      // Optimistic update
      setEvents(prev => prev.map(e => (e.id === formData.id) ? { ...e, ...formData } : e));
      try {
        const { error } = await supabase
          .from('scheduled_events')
          .update(eventPayload)
          .eq('id', formData.id)
          .eq('team_id', profile.team_id);
        if (error) throw error;
        toast.success('Event updated');
        fetchEvents();
      } catch (err) {
        toast.error('Failed to update event: ' + err.message);
        fetchEvents();
      }
    } else {
      // Optimistic create
      const tempId = `temp_${Date.now()}`;
      const payloads = [eventPayload];

      // Build extra payloads for each selected duplicate date
      if (formData.duplicateDates && formData.duplicateDates.length > 0) {
        const originalStart = new Date(formData.start_time);
        const originalEnd   = new Date(formData.end_time);
        // Duration in ms so we can reproduce it on each target date
        const durationMs    = originalEnd.getTime() - originalStart.getTime();
        const originDateKey = format(originalStart, 'yyyy-MM-dd');

        formData.duplicateDates.forEach(isoDate => {
          if (isoDate === originDateKey) return; // skip if user somehow has origin in the list

          // Transplant the original time onto the target calendar date
          const [year, month, day] = isoDate.split('-').map(Number);
          const newStart = new Date(originalStart);
          newStart.setFullYear(year, month - 1, day);

          const newEnd = new Date(newStart.getTime() + durationMs);

          payloads.push({
            ...eventPayload,
            start_time: newStart.toISOString(),
            end_time:   newEnd.toISOString(),
          });
        });
      }

      const newEvents = payloads.map((p, idx) => ({
        ...formData,
        id:         idx === 0 ? tempId : `temp_${Date.now()}_${idx}`,
        start_time: p.start_time,
        end_time:   p.end_time,
      }));

      setEvents(prev => [...prev, ...newEvents]);

      try {
        const { error } = await supabase
          .from('scheduled_events')
          .insert(payloads.map(p => ({ ...p, team_id: profile.team_id })));
        if (error) throw error;
        toast.success(payloads.length > 1 ? `Created ${payloads.length} events` : 'Event created');
        fetchEvents();
      } catch (err) {
        toast.error('Failed to create event: ' + err.message);
        setEvents(prev => prev.filter(e => !newEvents.some(ne => ne.id === e.id)));
      }
    }
  };

  const handleDeleteEvent = async (event, mode) => {
    // Safety Check: RBAC
    if (!canEditEvent(event)) {
      toast.error('You do not have permission to delete this event');
      return;
    }

    setModalOpen(false);
    // Optimistic delete
    setEvents(prev => prev.filter(e => e.id !== event.id));
    try {
      const { error } = await supabase
        .from('scheduled_events')
        .delete()
        .eq('id', event.id)
        .eq('team_id', profile.team_id);
      if (error) throw error;
      toast.success('Event deleted');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to delete: ' + err.message);
      fetchEvents();
    }
  };

  // ─── Drag/Resize Update ─────────────────────────────
  const handleEventUpdate = async (event, updates) => {
    // Optimistic update
    const evKey = event.instance_id || event.id;
    setEvents(prev => prev.map(e => {
      const eKey = e.instance_id || e.id;
      if (eKey === evKey) return { ...e, ...updates };
      return e;
    }));
    try {
      const { error } = await supabase
        .from('scheduled_events')
        .update({
          start_time: updates.start_time,
          end_time: updates.end_time,
        })
        .eq('id', event.id)
        .eq('team_id', profile.team_id);
      
      if (error) throw error;
      fetchEvents();
    } catch (err) {
      toast.error('Failed to update event: ' + err.message);
      fetchEvents();
    }
  };

  // ─── Publish ──────────────────────────────────────────
  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await api.post('/api/calendar-events/publish', {});
      toast.success(`Published ${res.data.published} events to main calendar`);
      fetchEvents();
    } catch {
      toast.error('Publish failed, please try again');
    }
    setPublishing(false);
  };

  // ─── Staged Templates Publish/Discard ─────────────────────────
  const [stagedPublishing, setStagedPublishing] = useState(false);
  const [stagedDiscarding, setStagedDiscarding] = useState(false);

  // Derive counts from staging
  const stagedDates = Object.keys(stagedTemplates);
  const hasStaged = stagedDates.length > 0;
  const stagedEventsCount = Object.values(stagedTemplates).reduce((acc, stage) => acc + (stage.payload?.events?.length || 0), 0);

  const handleStagedPublish = async () => {
    setStagedPublishing(true);
    try {
      await api.post('/api/templates/apply-bulk', { templates: stagedTemplates });
      toast.success(`Published ${stagedDates.length} templates (${stagedEventsCount} events)`);
      clearStagedTemplates();
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publish failed');
    }
    setStagedPublishing(false);
  };

  const handleStagedDiscard = async () => {
    setStagedDiscarding(true);
    clearStagedTemplates();
    toast.success('Staging area cleared');
    setStagedDiscarding(false);
  };

  // ─── Save Day as Template (from main schedule) ────
  const handleSaveAsTemplate = async () => {
    setSaveTemplateSaving(true);
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const name = saveTemplateName.trim() || `Template — ${format(currentDate, 'EEEE, MMM d')}`;
      const res = await api.post('/api/templates/from-day', { name, date: dateStr });
      toast.success(`Template "${res.data.name || name}" saved`);
      setSaveTemplateOpen(false);
      setSaveTemplateName('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.message || 'Failed to save template due to conflicts');
        console.error("Template Save Conflicts:", detail.conflicts);
      } else {
        toast.error(detail || 'Failed to save template');
      }
    }
    setSaveTemplateSaving(false);
  };

  // ─── Search ───────────────────────────────────────────
  const handleSearch = async () => {
    if (!searchEntityId) {
      toast.error('Please select an entity to search for');
      return;
    }
    const { start, end } = getViewRange(view, currentDate);
    const startStr = format(start, 'yyyy-MM-dd');
    const endStr = format(end, 'yyyy-MM-dd');

    const body = { start_date: startStr, end_date: endStr, search_type: searchType };
    if (searchType === 'faculty') body.faculty_id = searchEntityId;
    else if (searchType === 'batch') body.batch_id = searchEntityId;
    else if (searchType === 'assignment') body.assignment_id = searchEntityId;
    try {
      const res = await api.post('/api/calendar-events/search-slots', body);
      const slots = res.data.slots || [];
      setHighlights(slots);
      if (slots.length === 0) {
        toast.info('No free slots found in this range — fully booked or unavailable');
      } else {
        const totalMins = slots.reduce((s, sl) => s + sl.duration_minutes, 0);
        toast.success(`Found ${slots.length} free intervals (${Math.round(totalMins / 60 * 10) / 10}h total)`);
      }
    } catch { toast.error('Search failed'); }
  };

  const clearSearch = () => { setHighlights([]); setSearchOpen(false); setSearchEntityId(''); };

  // ─── Calendar visibility ──────────────────────────────
  const toggleCalendarVisibility = (calId) => {
    setVisibleCalendars(prev => {
      const next = new Set(prev);
      if (next.has(calId)) next.delete(calId); else next.add(calId);
      return next;
    });
  };

  const toggleAllCalendars = () => {
    const allOn = calendars.length > 0 && calendars.every(cal => visibleCalendars.has(cal.id));
    if (allOn) {
      setVisibleCalendars(new Set());
    } else {
      setVisibleCalendars(new Set(calendars.map(cal => cal.id)));
    }
  };

  const allFacultyVisible = useMemo(() => faculty.length > 0 && faculty.every(f => visibleCalendars.has(`faculty_${f.id}`)), [faculty, visibleCalendars]);
  const allFacultyAvail = useMemo(() => faculty.length > 0 && faculty.every(f => showAvailFor.has(`faculty_${f.id}`)), [faculty, showAvailFor]);

  const toggleAllFacultyVisibility = () => {
    setVisibleCalendars(prev => {
      const next = new Set(prev);
      if (allFacultyVisible) {
        faculty.forEach(f => next.delete(`faculty_${f.id}`));
      } else {
        faculty.forEach(f => next.add(`faculty_${f.id}`));
      }
      return next;
    });
  };

  const toggleAllFacultyAvailability = () => {
    setShowAvailFor(prev => {
      const next = new Set(prev);
      if (allFacultyAvail) {
        faculty.forEach(f => next.delete(`faculty_${f.id}`));
      } else {
        faculty.forEach(f => next.add(`faculty_${f.id}`));
      }
      return next;
    });
  };

  const allBatchesVisible = useMemo(() => batches.length > 0 && batches.every(b => visibleCalendars.has(`batch_${b.id}`)), [batches, visibleCalendars]);
  const allBatchesAvail = useMemo(() => batches.length > 0 && batches.every(b => showAvailFor.has(`batch_${b.id}`)), [batches, showAvailFor]);

  const toggleAllBatchesVisibility = () => {
    setVisibleCalendars(prev => {
      const next = new Set(prev);
      if (allBatchesVisible) {
        batches.forEach(b => next.delete(`batch_${b.id}`));
      } else {
        batches.forEach(b => next.add(`batch_${b.id}`));
      }
      return next;
    });
  };

  const toggleAllBatchesAvailability = () => {
    setShowAvailFor(prev => {
      const next = new Set(prev);
      if (allBatchesAvail) {
        batches.forEach(b => next.delete(`batch_${b.id}`));
      } else {
        batches.forEach(b => next.add(`batch_${b.id}`));
      }
      return next;
    });
  };

  // ─── Filtered events ─────────────────────────────────
  const filteredEvents = events.filter(ev => {
    // Hide draft events if toggle is on
    if (hideDraftEvents && ev.resolution === 'draft') return false;

    // If it's an availability event, check the showAvailFor Set
    if (ev.event_type === 'availability') {
      const key = `${ev.entity_type}_${ev.entity_id}`;
      return showAvailFor.has(key);
    }

    // Generic calendar visibility check: e.g. 'main', 'sandbox', 'algorithm'
    const hasVisibleCalendar = ev.calendar_ids
      ? ev.calendar_ids.some(cid => visibleCalendars.has(cid))
      : visibleCalendars.has('main');

    if (!hasVisibleCalendar) return false;

    // Gather all linked entities for robust toggling
    const fIds = new Set();
    const bIds = new Set();
    const brIds = new Set();

    if (ev.faculty_id) fIds.add(String(ev.faculty_id));
    if (ev.batch_id) bIds.add(String(ev.batch_id));
    if (ev.branch_id) brIds.add(String(ev.branch_id));

    if (ev.assignment_id && assignments && assignments.length > 0) {
      const assign = assignments.find(a => String(a.id) === String(ev.assignment_id));
      if (assign) {
        if (assign.faculty_id) fIds.add(String(assign.faculty_id));
        if (assign.batch_id) bIds.add(String(assign.batch_id));
        if (assign.branch_id) brIds.add(String(assign.branch_id));
      }
    }

    if (ev.calendar_ids && Array.isArray(ev.calendar_ids)) {
      ev.calendar_ids.forEach(cid => {
        if (typeof cid === 'string') {
          if (cid.startsWith('faculty_')) fIds.add(cid.substring(8));
          if (cid.startsWith('batch_')) bIds.add(cid.substring(6));
          if (cid.startsWith('branch_')) brIds.add(cid.substring(7));
        }
      });
    }

    if (batches && batches.length > 0) {
      bIds.forEach(bid => {
        const batch = batches.find(b => String(b.id) === String(bid));
        if (batch && batch.branch_id) {
          brIds.add(String(batch.branch_id));
        }
      });
    }

    const hasFaculty = fIds.size > 0;
    const hasBatch = bIds.size > 0;
    const hasBranch = brIds.size > 0;

    if (hasFaculty || hasBatch || hasBranch) {
      // For entity-linked events (lectures, etc.), when a calendar is explicitly toggled off, 
      // the event should be filtered out. So ALL linked categories must be currently visible.
      const facultyVisible = !hasFaculty || Array.from(fIds).some(fid => visibleCalendars.has(`faculty_${fid}`));
      const batchVisible = !hasBatch || Array.from(bIds).some(bid => visibleCalendars.has(`batch_${bid}`));
      const branchVisible = !hasBranch || Array.from(brIds).some(brid => visibleCalendars.has(`branch_${brid}`));

      return facultyVisible && batchVisible && branchVisible;
    }

    return true;
  }).map(ev => {
    // Override color for algorithmic/sandbox events using assignments
    return ev;
  });

  const selectedDayEvents = useMemo(() => {
    if (!selectedDayForModal) return null;
    const d = typeof selectedDayForModal === 'string' ? parseISO(selectedDayForModal) : selectedDayForModal;
    const dayEvs = filteredEvents.filter(ev => {
      const s = typeof ev.start_time === 'string' ? parseISO(ev.start_time) : ev.start_time;
      return isSameDay(s, d);
    }).sort((a, b) => {
      const as = typeof a.start_time === 'string' ? parseISO(a.start_time) : a.start_time;
      const bs = typeof b.start_time === 'string' ? parseISO(b.start_time) : b.start_time;
      return as - bs;
    });
    return { day: d, events: dayEvs };
  }, [selectedDayForModal, filteredEvents]);

  // ─── View helpers ─────────────────────────────────────
  const getViewDays = () => {
    if (view === 'day') return [currentDate];
    if (view === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    return [];
  };

  const getViewTitle = () => {
    if (view === 'month' || view === 'agenda') return format(currentDate, 'MMMM yyyy');
    if (view === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      if (start.getMonth() === end.getMonth()) return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'EEEE, MMMM d, yyyy');
  };

  return (
    <div className="flex h-full overflow-hidden relative bg-zinc-50 dark:bg-zinc-950" data-testid="calendar-panel">
      {/* ─── Sidebar (Drawer on Mobile) ─── */}
      <>
        {isMobile && sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/40 z-[100] backdrop-blur-[2px] transition-all animate-fade-in" 
            onClick={() => setSidebarOpen(false)} 
          />
        )}
        <div 
          className={cn(
            "flex flex-col h-full overflow-hidden border-r border-[var(--sidebar-border)] shrink-0 relative z-[110]", 
            !isResizing && "transition-all duration-300",
            isMobile 
              ? "fixed inset-y-0 left-0 shadow-2xl" 
              : (sidebarOpen ? "" : "w-0 min-w-0 border-none")
          )} 
          style={{ 
            background: 'var(--sidebar-bg)', 
            color: 'var(--sidebar-fg)',
            width: isMobile ? (sidebarOpen ? '280px' : '0') : (sidebarOpen ? `${sidebarWidth}px` : '0'),
            transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)'
          }}
        >
          {/* Resize Handle (Desktop Only) */}
          {!isMobile && sidebarOpen && (
            <div
              className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-violet-500/30 transition-colors z-[100] group"
              onMouseDown={startResizing}
            >
              <div className={cn("absolute right-0.5 top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-full bg-zinc-300 dark:bg-zinc-600 transition-opacity", isResizing ? "opacity-100 bg-violet-400" : "opacity-0 group-hover:opacity-100")} />
            </div>
          )}

          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <div className="p-4 border-b border-[var(--sidebar-border)]">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <span className="font-black tracking-tighter text-lg">SCHEDULER</span>
                </div>
                {isMobile && (
                  <button onClick={() => setSidebarOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 bg-zinc-50 dark:bg-zinc-900/50 p-2 rounded-xl border border-zinc-200/50 dark:border-zinc-800/50">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md text-sm font-black text-white uppercase">
                  {(username || 'A').charAt(0)}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-bold truncate leading-tight">{username || 'User'}</span>
                  <span className="text-[9px] font-black uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
                    {profile.access_level === 1 ? 'Super Admin' : 'Administrator'}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-3 space-y-4">
              <div className="bg-zinc-50 dark:bg-zinc-900/50 rounded-2xl p-2 border border-zinc-200/50 dark:border-zinc-800/50 shadow-sm">
                <MiniCalendar currentDate={currentDate} selectedDate={currentDate} onDateSelect={(d) => { setCurrentDate(d); setView('day'); if(isMobile) setSidebarOpen(false); }} />
              </div>

              <SidebarSection
                title="Faculty"
                icon={User}
                defaultOpen={false}
                action={
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={toggleAllFacultyAvailability}
                      className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md"
                      title="Toggle All Faculty Availability"
                    >
                      <CalIcon className={cn("w-3.5 h-3.5 transition-colors", allFacultyAvail ? "text-emerald-500" : "text-zinc-400")} />
                    </button>
                    <button
                      onClick={toggleAllFacultyVisibility}
                      className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md"
                      title="Toggle All Faculty Visibility"
                    >
                      <CheckCircle2 className={cn("w-3.5 h-3.5 transition-colors", allFacultyVisible ? "text-blue-500" : "text-zinc-400")} />
                    </button>
                  </div>
                }
              >
                {faculty.map(f => {
                  const isVisible = visibleCalendars.has(`faculty_${f.id}`);
                  const isAvail = showAvailFor.has(`faculty_${f.id}`);
                  return (
                    <div
                      key={f.id}
                      className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800/50 cursor-pointer group"
                      onClick={() => toggleCalendarVisibility(`faculty_${f.id}`)}
                    >
                      <span className={cn("text-xs flex-1 truncate transition-opacity", isVisible ? "font-bold" : "opacity-50")}>
                        {f.name}
                      </span>
                      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => toggleAvailVisibility(`faculty_${f.id}`)}
                          className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded transition-colors"
                          title="Toggle Availability Events"
                        >
                          <CalIcon className={cn("w-3 h-3 transition-colors", isAvail ? "text-emerald-500 font-bold" : "text-zinc-600 opacity-50")} />
                        </button>
                        <button
                          onClick={() => toggleCalendarVisibility(`faculty_${f.id}`)}
                          className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded transition-colors"
                          title="Toggle Visibility"
                        >
                          {isVisible ? <Eye className="w-3 h-3 text-zinc-400" /> : <EyeOff className="w-3 h-3 text-zinc-600" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </SidebarSection>

              <SidebarSection
                title="Batches"
                icon={Users}
                defaultOpen={false}
                action={
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={toggleAllBatchesAvailability}
                      className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md"
                      title="Toggle All Batches Availability"
                    >
                      <CalIcon className={cn("w-3.5 h-3.5 transition-colors", allBatchesAvail ? "text-emerald-500" : "text-zinc-400")} />
                    </button>
                    <button
                      onClick={toggleAllBatchesVisibility}
                      className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md"
                      title="Toggle All Batches Visibility"
                    >
                      <CheckCircle2 className={cn("w-3.5 h-3.5 transition-colors", allBatchesVisible ? "text-blue-500" : "text-zinc-400")} />
                    </button>
                  </div>
                }
              >
                {batches.map(b => {
                  const isVisible = visibleCalendars.has(`batch_${b.id}`);
                  const isAvail = showAvailFor.has(`batch_${b.id}`);
                  return (
                    <div
                      key={b.id}
                      className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800/50 cursor-pointer group"
                      onClick={() => toggleCalendarVisibility(`batch_${b.id}`)}
                    >
                      <span className={cn("text-xs flex-1 truncate transition-opacity", isVisible ? "font-bold" : "opacity-50")}>
                        {b.name}
                      </span>
                      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => toggleAvailVisibility(`batch_${b.id}`)}
                          className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded transition-colors"
                          title="Toggle Availability Events"
                        >
                          <CalIcon className={cn("w-3 h-3 transition-colors", isAvail ? "text-emerald-500 font-bold" : "text-zinc-600 opacity-50")} />
                        </button>
                        <button
                          onClick={() => toggleCalendarVisibility(`batch_${b.id}`)}
                          className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded transition-colors"
                          title="Toggle Visibility"
                        >
                          {isVisible ? <Eye className="w-3 h-3 text-zinc-400" /> : <EyeOff className="w-3 h-3 text-zinc-600" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </SidebarSection>

              <div className="pt-2 border-t border-zinc-200 dark:border-zinc-800">
                <SidebarSection title="Templates" icon={Layout} defaultOpen={false}>
                  <TemplatesSection faculty={faculty} batches={batches} currentDate={currentDate} />
                </SidebarSection>
              </div>

              <SidebarSection title="AI Engine" icon={Cpu} defaultOpen={false}>
                <AlgorithmSection setVisibleCalendars={setVisibleCalendars} />
              </SidebarSection>
            </div>
          </div>
          <div className="p-4 border-t border-[var(--sidebar-border)] text-[10px] font-black uppercase tracking-widest text-zinc-500 opacity-50">
            ScholarsAnchor OS v3.0
          </div>
        </div>
      </>

      {/* ─── Main Area ─────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        
        {/* Mobile Header */}
        {isMobile && (
          <div className="h-14 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between px-4 shrink-0 z-[40]">
            <div className="flex items-center gap-2">
              <button onClick={() => setMobileSidebarOpen(true)} className="w-10 h-10 flex items-center justify-center -ml-1 text-zinc-600 dark:text-zinc-400">
                <ChevronsRight className="w-5 h-5" />
              </button>
              <button onClick={() => setSidebarOpen(true)} className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                <Layout className="w-4 h-4" />
              </button>
              <h2 className="text-xs font-black tracking-tight dark:text-zinc-100 ml-1">
                {format(currentDate, 'MMM yyyy')}
              </h2>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => navigate(-1)} className="p-2 text-zinc-500"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={() => setCurrentDate(new Date())} className="px-2 text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">Today</button>
              <button onClick={() => navigate(1)} className="p-2 text-zinc-500"><ChevronRight className="w-5 h-5" /></button>
            </div>
          </div>
        )}

        {/* Desktop Toolbar */}
        {!isMobile && (
          <div className="h-16 flex items-center justify-between px-6 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shrink-0 z-40">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl">
                <button onClick={() => setSidebarOpen(!sidebarOpen)} className={cn("p-1.5 rounded-lg transition-all", sidebarOpen ? "bg-white dark:bg-zinc-700 text-blue-600 shadow-sm" : "text-zinc-500")}><Layout className="w-4 h-4" /></button>
              </div>
              <div className="flex flex-col">
                <h1 className="text-lg font-black tracking-tighter dark:text-zinc-100 leading-none">{getViewTitle()}</h1>
                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mt-1">{view} view</span>
              </div>
              <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl ml-2">
                <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest text-zinc-600 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-700 transition-all">Today</button>
                <button onClick={() => navigate(1)} className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl">
                {VIEWS.map(v => (
                  <button key={v.id} onClick={() => setView(v.id)} className={cn("px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all", view === v.id ? "bg-white dark:bg-zinc-700 text-blue-600 shadow-sm" : "text-zinc-400 hover:text-zinc-600")}>
                    {v.label}
                  </button>
                ))}
              </div>
              <div className="w-[1px] h-6 bg-zinc-200 dark:bg-zinc-800 mx-1" />
              <button onClick={() => openNewEvent()} className="h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-xl shadow-lg shadow-blue-500/20 active:scale-95 transition-all flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Event
              </button>
            </div>
          </div>
        )}

        {/* View Selection Tabs for Mobile */}
        {isMobile && (
          <div className="flex bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 shrink-0">
            {VIEWS.map(v => (
              <button key={v.id} onClick={() => setView(v.id)} className={cn("flex-1 py-3 text-[9px] font-black uppercase tracking-widest transition-all", view === v.id ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/30 dark:bg-blue-900/10" : "text-zinc-400")}>
                {v.label}
              </button>
            ))}
          </div>
        )}

        {/* Staged Changes Notification Bar */}
        {hasStaged && (
          <div className="px-4 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-between text-white shadow-lg animate-fade-in relative z-50">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-[0.15em]">{stagedEventsCount} Changes Staged</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleStagedDiscard} className="text-[9px] font-black uppercase tracking-widest bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-all">Discard</button>
              <button onClick={handleStagedPublish} className="text-[9px] font-black uppercase tracking-widest bg-white text-violet-600 px-3 py-1.5 rounded-lg font-bold shadow-sm active:scale-95 transition-all">Publish</button>
            </div>
          </div>
        )}

        {/* Search & Tool Buttons (Combined on Mobile) */}
        <div className="px-4 py-2 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between gap-2 overflow-x-auto no-scrollbar shrink-0">
          <div className="flex items-center gap-2">
            <button onClick={() => setSchedulerSidebarOpen(!schedulerSidebarOpen)} className={cn("h-8 px-3 rounded-lg flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all", schedulerSidebarOpen ? "bg-violet-100 dark:bg-violet-900/30 text-violet-600" : "bg-zinc-50 dark:bg-zinc-800 text-zinc-500")}>
              <Sparkles className="w-3.5 h-3.5" />
              <span className={isMobile ? "hidden" : "inline"}>AI Assistant</span>
            </button>
            <button onClick={() => setSearchOpen(!searchOpen)} className={cn("h-8 px-3 rounded-lg flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all", searchOpen ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600" : "bg-zinc-50 dark:bg-zinc-800 text-zinc-500")}>
              <Search className="w-3.5 h-3.5" />
              <span className={isMobile ? "hidden" : "inline"}>Search Free</span>
            </button>
            {(() => {
              const isInFlight = buttonState !== 'idle';
              const isProgressBar = buttonState === 'running' || buttonState === 'completing';

              // Derive progress from Zustand job list
              const activeJob = activeJobId
                ? useCalendarStore.getState().schedulingJobs.find(j => j.id === activeJobId)
                : null;
              const pct = buttonState === 'completing'
                ? 100
                : Math.min(90, Math.round((activeJob?.events_added || 0) / 2));

              // Conflict-mode overrides: never show progress bar for conflicts
              const isConflictMode = conflictEventIds.size > 0;

              if (isProgressBar && !isConflictMode) {
                return (
                  <div
                    data-testid="sched-progress-bar"
                    className="relative h-8 rounded-lg overflow-hidden"
                    style={{ minWidth: '180px' }}
                    title={`${pct}% complete · ${activeJob?.events_added || 0} events generated`}
                  >
                    {/* Track */}
                    <div className="absolute inset-0 bg-zinc-100 dark:bg-zinc-800 rounded-lg" />
                    {/* Fill */}
                    <div
                      className={cn(
                        "absolute inset-0 rounded-lg transition-all duration-700 ease-out",
                        buttonState === 'completing'
                          ? "bg-emerald-500"
                          : "bg-gradient-to-r from-violet-500 via-indigo-500 to-purple-500"
                      )}
                      style={{ width: `${pct}%` }}
                    />
                    {/* Label */}
                    <div className="absolute inset-0 flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-white drop-shadow">
                      {buttonState === 'completing' ? (
                        <CheckCircle2 className="w-3 h-3 shrink-0" />
                      ) : (
                        <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                      )}
                      <span>
                        {buttonState === 'completing'
                          ? `Done · ${activeJob?.events_added || 0} events`
                          : `${pct}% · ${activeJob?.events_added || 0} events`}
                      </span>
                    </div>
                  </div>
                );
              }

              return (
                <button
                  data-testid="sched-main-btn"
                  onClick={isConflictMode ? handleTriggerUpdate : handleInitializeOrUpdateClick}
                  disabled={updatingSchedule || processingChanges || isInFlight || !!activeJobId}
                  className={cn(
                    "h-8 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 active:scale-95 transition-all disabled:opacity-50 text-white shadow-lg",
                    (updatingSchedule || processingChanges || isInFlight || !!activeJobId)
                      ? "bg-violet-500 shadow-violet-500/10"
                      : isConflictMode
                        ? "bg-rose-600 hover:bg-rose-700 shadow-rose-600/20 animate-pulse"
                        : !isInitialized
                          ? "bg-violet-600 hover:bg-violet-700 shadow-violet-600/20"
                          : sigChanged
                            ? "bg-amber-500 hover:bg-amber-600 shadow-amber-500/20 animate-pulse"
                            : "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/10"
                  )}
                  title={
                    isConflictMode
                      ? `Process ${conflictEventIds.size} conflicting lecture(s)`
                      : buttonState === 'preflight'
                        ? 'Validating configuration…'
                        : !isInitialized
                          ? 'Start the initial AI schedule generation'
                          : sigChanged
                            ? 'Configuration changed since last sync — click to regenerate'
                            : 'Sync updates with scheduling engine'
                  }
                >
                  {(updatingSchedule || processingChanges || isInFlight) ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : isConflictMode ? (
                    <AlertTriangle className="w-3.5 h-3.5" />
                  ) : !isInitialized ? (
                    <Zap className="w-3.5 h-3.5" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  <span>
                    {buttonState === 'preflight'
                      ? 'Checking…'
                      : isConflictMode
                        ? `Process Changes (${conflictEventIds.size})`
                        : !isInitialized
                          ? 'Run Initialization'
                          : 'Sync Updates'}
                  </span>
                </button>
              );
            })()}

            {/* Version History Icon — opens past scheduling runs modal */}
            <button
              onClick={() => {
                setIsVersionModalOpen(true);
                fetchVersionHistory();
              }}
              title="Schedule version history"
              className={cn(
                "h-8 w-8 rounded-lg flex items-center justify-center transition-all active:scale-95",
                isVersionModalOpen
                  ? "bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400"
                  : "bg-zinc-50 dark:bg-zinc-800 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700"
              )}
            >
              <History className="w-3.5 h-3.5" />
            </button>

            {/* Unlock locked conflicts toggle — only visible when locked events have conflicts */}
            {lockedConflictIds.size > 0 && (
              <button
                onClick={handleUnlockLockedConflicts}
                disabled={unlockingLocked}
                className="h-8 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 active:scale-95 transition-all disabled:opacity-50 bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20"
                title={`${lockedConflictIds.size} locked event(s) have scheduling conflicts — click to unlock so they can be repaired`}
              >
                {unlockingLocked ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 018 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                )}
                <span>Unlock Locked ({lockedConflictIds.size})</span>
              </button>
            )}

            {/* ── Drift / Advance-Window Banner — hidden while any job runs ── */}
            {driftDays > 0 && isInitialized && !activeJobId && (
              <button
                data-testid="sched-drift-btn"
                onClick={handleAdvanceWindow}
                disabled={advancingWindow || !!activeJobId}
                title={`Schedule is ${driftDays} day(s) behind today — click to promote drafts to solid and extend the draft horizon`}
                className={cn(
                  "h-8 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 active:scale-95 transition-all disabled:opacity-60",
                  "bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20 animate-pulse"
                )}
              >
                {advancingWindow ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                ) : (
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                )}
                <span>
                  {advancingWindow
                    ? 'Advancing…'
                    : `Update schedule for ${driftDays} day${driftDays !== 1 ? 's' : ''} from last sync`}
                </span>
              </button>
            )}

          </div>
          <div className="flex items-center gap-2">
            {painterMode ? (
              <button onClick={() => setPainterMode(null)} className="h-8 px-3 bg-gradient-to-r from-emerald-500 to-rose-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-2 animate-pulse">
                <Paintbrush className="w-3.5 h-3.5" />
                <span>Stop Paint</span>
              </button>
            ) : (
              <button onClick={() => setPainterDropOpen(!painterDropOpen)} className="h-8 px-3 bg-zinc-50 dark:bg-zinc-800 text-zinc-500 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                <Paintbrush className="w-3.5 h-3.5" />
                <span className={isMobile ? "hidden" : "inline"}>Paint Avail</span>
              </button>
            )}
            <button onClick={() => setSaveTemplateOpen(true)} className="h-8 px-3 bg-zinc-50 dark:bg-zinc-800 text-zinc-500 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
              <Save className="w-3.5 h-3.5" />
              <span className={isMobile ? "hidden" : "inline"}>Save Day</span>
            </button>
            <div className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-800/60 px-3 h-8 rounded-xl border border-zinc-200/50 dark:border-zinc-800/80 select-none transition-all">
              <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Hide Drafts</span>
              <button
                role="switch"
                aria-checked={hideDraftEvents}
                onClick={() => setHideDraftEvents(prev => !prev)}
                className={cn(
                  "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none",
                  hideDraftEvents ? "bg-amber-500" : "bg-zinc-300 dark:bg-zinc-700"
                )}
                title={hideDraftEvents ? 'Showing draft events' : 'Hiding draft events'}
              >
                <span
                  className="pointer-events-none inline-block h-3.5 w-3.5 rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out"
                  style={{ transform: hideDraftEvents ? 'translateX(18px)' : 'translateX(2px)' }}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Search Overlay/Bar */}
        {searchOpen && (
          <div className="px-4 py-3 bg-amber-50/50 dark:bg-amber-900/10 border-b border-amber-200 dark:border-amber-800 flex flex-col md:flex-row gap-3 animate-fade-in relative z-30">
            <div className="flex rounded-lg overflow-hidden border border-amber-200 dark:border-amber-800 shrink-0">
              {['faculty', 'batch', 'assignment'].map(t => (
                <button key={t} onClick={() => setSearchType(t)} className={cn("flex-1 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest transition-all", searchType === t ? "bg-amber-500 text-white" : "bg-white dark:bg-zinc-800 text-zinc-500")}>
                  {t}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-1">
              <select value={searchEntityId} onChange={e => setSearchEntityId(e.target.value)} className="flex-1 h-9 rounded-lg border border-amber-200 dark:border-amber-800 bg-white dark:bg-zinc-800 text-xs px-3 focus:ring-2 focus:ring-amber-500/20 outline-none">
                <option value="">Select {searchType}...</option>
                {searchType === 'faculty' && faculty.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                {searchType === 'batch' && batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                {searchType === 'assignment' && assignments.map(a => <option key={a.id} value={a.id}>{a.faculty_name} - {a.batch_name}</option>)}
              </select>
              <button onClick={handleSearch} disabled={!searchEntityId} className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest shadow-lg shadow-amber-500/20 transition-all active:scale-95">Find</button>
              <button onClick={clearSearch} className="h-9 w-9 flex items-center justify-center rounded-lg bg-white dark:bg-zinc-800 border border-amber-200 dark:border-amber-800 text-amber-600"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {/* Painter Dropdown Overlay */}
        {painterDropOpen && !painterMode && (
          <div className="absolute top-14 right-4 w-72 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl z-[100] p-4 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Paint Availability</span>
              <button onClick={() => setPainterDropOpen(false)}><X className="w-4 h-4 text-zinc-500" /></button>
            </div>
            <div className="space-y-3">
              <select value={painterForm.entity_type || ''} onChange={e => setPainterForm(f => ({ ...f, entity_type: e.target.value, entity_id: '' }))} className="w-full h-10 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50 text-xs px-3">
                <option value="faculty">Faculty</option>
                <option value="batch">Batch</option>
              </select>
              <select value={painterForm.entity_id || ''} onChange={e => setPainterForm(f => ({ ...f, entity_id: e.target.value }))} className="w-full h-10 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50 text-xs px-3">
                <option value="">Select {painterForm.entity_type}...</option>
                {(painterForm.entity_type === 'faculty' ? faculty : batches).map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button onClick={() => setPainterForm(f => ({ ...f, availability_mode: 'unavailable' }))} className={cn("flex-1 h-9 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", painterForm.availability_mode === 'unavailable' ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500')}>Unavailable</button>
                <button onClick={() => setPainterForm(f => ({ ...f, availability_mode: 'preferred' }))} className={cn("flex-1 h-9 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all", painterForm.availability_mode === 'preferred' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500')}>Preferred</button>
              </div>
              <button disabled={!painterForm.entity_id} onClick={() => {
                const list = painterForm.entity_type === 'faculty' ? faculty : batches;
                const entity = list.find(e => e.id === painterForm.entity_id);
                setPainterMode({
                  entity_type: painterForm.entity_type,
                  entity_id: painterForm.entity_id,
                  entity_name: entity?.name || '',
                  availability_mode: painterForm.availability_mode,
                });
                setPainterDropOpen(false);
              }} className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-500/20 active:scale-95 transition-all disabled:opacity-50">Start Painting</button>
            </div>
          </div>
        )}

        {/* Calendar Grid Container */}
        <div className="flex-1 overflow-hidden min-h-0 bg-white dark:bg-zinc-950 flex flex-col">
          {(view === 'week' || view === 'day') && (
            <TimeGrid
              events={filteredEvents}
              allEvents={events}
              days={getViewDays()}
              onSlotClick={(day, hour) => openNewEvent(day, hour)}
              onEventClick={handleEventClick}
              onDragCreate={(day, startMin, endMin) => openNewEventFromDrag(day, startMin, endMin)}
              onEventUpdate={handleEventUpdate}
              onTemplateDrop={(payload, day, startMin) => setStagedTemplate(format(day, 'yyyy-MM-dd'), { templateId: payload.id, session_id: payload.session_id, payload })}
              onStagedDiscard={(dayStr) => removeStagedTemplate(dayStr)}
              stagedTemplates={stagedTemplates}
              highlights={highlights}
              currentTime={currentTime}
              showAvailFor={showAvailFor}
              painterMode={painterMode}
              faculty={faculty}
              batches={batches}
              isMobile={isMobile}
              conflictEventIds={conflictEventIds}
            />
          )}
          {view === 'month' && (
            <MonthGrid
              events={filteredEvents}
              currentDate={currentDate}
              onDayClick={(d) => setSelectedDayForModal(d)}
              onEventClick={handleEventClick}
              faculty={faculty}
              batches={batches}
              isMobile={isMobile}
              conflictEventIds={conflictEventIds}
            />
          )}
          {view === 'agenda' && (
            <AgendaView
              events={filteredEvents}
              currentDate={currentDate}
              onEventClick={handleEventClick}
              faculty={faculty}
              batches={batches}
              isMobile={isMobile}
              conflictEventIds={conflictEventIds}
            />
          )}
        </div>

        {/* Mobile Floating Action Button */}
        {isMobile && (
          <button onClick={openNewEvent} className="fixed bottom-20 right-6 w-14 h-14 rounded-full bg-blue-600 text-white shadow-2xl shadow-blue-500/40 flex items-center justify-center z-[60] active:scale-90 transition-transform hover:bg-blue-700">
            <Plus className="w-8 h-8" />
          </button>
        )}
      </div>

      {/* ─── Scheduler Sidebar (right) ─────────────── */}
      {schedulerSidebarOpen && (
        <>
          {isMobile && (
            <div className="fixed inset-0 bg-black/40 z-[120] backdrop-blur-[2px]" onClick={() => setSchedulerSidebarOpen(false)} />
          )}
          <div className={cn(
            "fixed md:relative top-0 right-0 h-full bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800 z-[130] transition-all duration-300",
            isMobile ? "w-[85%] max-w-[350px]" : "w-80"
          )}>
            <div className="h-full flex flex-col">
              <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">AI Scheduler Assistant</span>
                <button onClick={() => setSchedulerSidebarOpen(false)} className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md">
                  <X className="w-4 h-4 text-zinc-500" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <SchedulerSidebar />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Run Initialization Options Modal */}
      {initModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-fade-in">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 w-full max-w-md shadow-2xl border border-zinc-200 dark:border-zinc-800 animate-scale-in space-y-6">
            <div className="space-y-1">
              <h3 className="text-lg font-black tracking-tighter dark:text-white uppercase flex items-center gap-2">
                <Zap className="w-5 h-5 text-violet-500" />
                Initialize AI Scheduler
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Configure your autonomous scheduling parameters before launching the optimization engine.
              </p>
            </div>

            <div className="space-y-6">
              {/* Allotment Start Date */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold dark:text-zinc-200">Allotment Start Date</span>
                </div>
                <input
                  type="date"
                  value={initStartDate}
                  onChange={e => setInitStartDate(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 text-xs text-zinc-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all font-medium"
                />
                <p className="text-[10px] text-zinc-400 dark:text-zinc-500">
                  Lectures will be scheduled starting from this date. Courses starting in the future will only be scheduled post their start date.
                </p>
              </div>

              {/* Lock hours */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold dark:text-zinc-200">Schedule Lock-in Period</span>
                  <span className="text-xs font-bold font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded leading-none">
                    {initLockHours} hrs
                  </span>
                </div>
                <input
                  type="range" min="6" max="168"
                  value={initLockHours}
                  onChange={e => setInitLockHours(parseInt(e.target.value) || 24)}
                  className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="relative w-full h-5 mt-1">
                  <div className="absolute top-0 left-0 w-full h-1 flex pointer-events-none">
                    {[6, 24, 72, 168].map((val) => {
                      const percent = ((val - 6) / (168 - 6)) * 100;
                      return (
                        <div
                          key={val}
                          className="absolute w-0.5 h-1 bg-zinc-200 dark:bg-zinc-800 -translate-x-1/2"
                          style={{ left: `${percent}%` }}
                        />
                      );
                    })}
                  </div>
                  <div className="absolute top-2 left-0 w-full">
                    {[
                      { value: 6, label: '6 Hours' },
                      { value: 24, label: '24 Hours (1 Day)' },
                      { value: 72, label: '72 Hours (3 Days)' },
                      { value: 168, label: '168 Hours (1 Week)' }
                    ].map((tick, index, arr) => {
                      const percent = ((tick.value - 6) / (168 - 6)) * 100;
                      let alignmentClass = "-translate-x-1/2";
                      if (index === 0) alignmentClass = "translate-x-0";
                      if (index === arr.length - 1) alignmentClass = "-translate-x-full";
                      return (
                        <span
                          key={tick.value}
                          className={cn(
                            "absolute text-[9px] text-zinc-400 dark:text-zinc-500 font-medium whitespace-nowrap transition-all duration-200",
                            alignmentClass
                          )}
                          style={{ left: `${percent}%` }}
                        >
                          {tick.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Rolling Window */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold dark:text-zinc-200">Rolling Window (Solidified Weeks)</span>
                  <span className="text-xs font-bold font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded leading-none">
                    {initRollingWeeks} weeks
                  </span>
                </div>
                <input
                  type="range" min="1" max="16"
                  value={initRollingWeeks}
                  onChange={e => setInitRollingWeeks(parseInt(e.target.value) || 4)}
                  className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="relative w-full h-5 mt-1">
                  <div className="absolute top-0 left-0 w-full h-1 flex pointer-events-none">
                    {[1, 4, 8, 16].map((val) => {
                      const percent = ((val - 1) / (16 - 1)) * 100;
                      return (
                        <div
                          key={val}
                          className="absolute w-0.5 h-1 bg-zinc-200 dark:bg-zinc-800 -translate-x-1/2"
                          style={{ left: `${percent}%` }}
                        />
                      );
                    })}
                  </div>
                  <div className="absolute top-2 left-0 w-full">
                    {[
                      { value: 1, label: '1 Week' },
                      { value: 4, label: '4 Weeks (1 Month)' },
                      { value: 8, label: '8 Weeks (2 Months)' },
                      { value: 16, label: '16 Weeks' }
                    ].map((tick, index, arr) => {
                      const percent = ((tick.value - 1) / (16 - 1)) * 100;
                      let alignmentClass = "-translate-x-1/2";
                      if (index === 0) alignmentClass = "translate-x-0";
                      if (index === arr.length - 1) alignmentClass = "-translate-x-full";
                      return (
                        <span
                          key={tick.value}
                          className={cn(
                            "absolute text-[9px] text-zinc-400 dark:text-zinc-500 font-medium whitespace-nowrap transition-all duration-200",
                            alignmentClass
                          )}
                          style={{ left: `${percent}%` }}
                        >
                          {tick.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Draft Resolution */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold dark:text-zinc-200">Draft Resolution Window</span>
                  <span className="text-xs font-bold font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded leading-none">
                    {initDraftWeeks} weeks
                  </span>
                </div>
                <input
                  type="range" min="1" max="8"
                  value={initDraftWeeks}
                  onChange={e => setInitDraftWeeks(parseInt(e.target.value) || 2)}
                  className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="relative w-full h-5 mt-1">
                  <div className="absolute top-0 left-0 w-full h-1 flex pointer-events-none">
                    {[1, 2, 4, 8].map((val) => {
                      const percent = ((val - 1) / (8 - 1)) * 100;
                      return (
                        <div
                          key={val}
                          className="absolute w-0.5 h-1 bg-zinc-200 dark:bg-zinc-800 -translate-x-1/2"
                          style={{ left: `${percent}%` }}
                        />
                      );
                    })}
                  </div>
                  <div className="absolute top-2 left-0 w-full">
                    {[
                      { value: 1, label: '1 Week' },
                      { value: 2, label: '2 Weeks' },
                      { value: 4, label: '4 Weeks' },
                      { value: 8, label: '8 Weeks' }
                    ].map((tick, index, arr) => {
                      const percent = ((tick.value - 1) / (8 - 1)) * 100;
                      let alignmentClass = "-translate-x-1/2";
                      if (index === 0) alignmentClass = "translate-x-0";
                      if (index === arr.length - 1) alignmentClass = "-translate-x-full";
                      return (
                        <span
                          key={tick.value}
                          className={cn(
                            "absolute text-[9px] text-zinc-400 dark:text-zinc-500 font-medium whitespace-nowrap transition-all duration-200",
                            alignmentClass
                          )}
                          style={{ left: `${percent}%` }}
                        >
                          {tick.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 h-11 rounded-xl text-xs font-bold" onClick={() => setInitModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 h-11 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold shadow-lg shadow-violet-500/20" 
                onClick={handleConfirmInitialization}
              >
                Launch Scheduler
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      <EventModal
        event={editEvent}
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditEvent(null); }}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
        faculty={faculty}
        batches={batches}
        subjects={subjects}
        branches={branches}
        assignments={assignments}
      />

      {/* Save Template Dialog */}
      {saveTemplateOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-fade-in">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 w-full max-w-sm shadow-2xl border border-zinc-200 dark:border-zinc-800 animate-scale-in">
            <h3 className="text-lg font-black tracking-tighter mb-4 dark:text-white uppercase">Save Day as Template</h3>
            <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
              This will capture all events on <span className="font-bold text-zinc-800 dark:text-zinc-200">{format(currentDate, 'MMMM d, yyyy')}</span> as a reusable scheduling pattern.
            </p>
            <input
              type="text"
              placeholder="Template Name (e.g., Monday Regular)"
              className="w-full h-12 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-4 mb-6 outline-none focus:ring-2 focus:ring-blue-500/20 text-sm"
              value={saveTemplateName}
              onChange={e => setSaveTemplateName(e.target.value)}
              autoFocus
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 h-11 rounded-xl text-xs font-bold" onClick={() => setSaveTemplateOpen(false)}>Cancel</Button>
              <Button className="flex-1 h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-lg shadow-blue-500/20" onClick={handleSaveAsTemplate} disabled={saveTemplateSaving}>
                {saveTemplateSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Template"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Day Events Modal */}
      {selectedDayEvents && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[150] p-4 animate-fade-in">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 w-full max-w-lg shadow-2xl border border-zinc-200 dark:border-zinc-800 animate-scale-in flex flex-col max-h-[80vh]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 border-b border-zinc-100 dark:border-zinc-800 pb-3">
              <div className="flex flex-col">
                <h3 className="text-lg font-black tracking-tighter dark:text-white uppercase">
                  {format(selectedDayEvents.day, 'eeee')}
                </h3>
                <span className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">
                  {format(selectedDayEvents.day, 'MMMM d, yyyy')}
                </span>
              </div>
              <button 
                onClick={() => setSelectedDayForModal(null)} 
                className="w-8 h-8 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center text-zinc-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Scrollable list of cards */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1 py-1 custom-scrollbar">
              {selectedDayEvents.events.length === 0 ? (
                <div className="text-center py-12 text-zinc-400">
                  <div className="text-4xl mb-3">📅</div>
                  <p className="text-sm font-medium mb-1">No events scheduled for this day</p>
                  <p className="text-xs opacity-60">Click "Schedule Event" to add a new slot</p>
                </div>
              ) : (
                selectedDayEvents.events.map(ev => {
                  const s = typeof ev.start_time === 'string' ? parseISO(ev.start_time) : ev.start_time;
                  const e = typeof ev.end_time === 'string' ? parseISO(ev.end_time) : ev.end_time;
                  const isConflict = conflictEventIds.has(String(ev.instance_id || ev.id));
                  const bgColor = getColor(ev.color);
                  const timeStr = ev.is_all_day
                    ? 'All day'
                    : `${format(s, 'h:mm a')} – ${format(e, 'h:mm a')}`;
                    
                  // Resolve names
                  let fId = ev.faculty_id || ev.entity_id;
                  let bId = ev.batch_id || ev.entity_id;

                  if (!fId && !bId && ev.assignment_id && assignments?.length > 0) {
                    const assign = assignments.find(a => String(a.id) === String(ev.assignment_id));
                    if (assign) {
                      fId = assign.faculty_id;
                      bId = assign.batch_id;
                    }
                  }

                  const fName = faculty.find(f => String(f.id) === String(fId))?.name;
                  
                  // Resolve all batch names
                  const batchIds = new Set();
                  if (bId) batchIds.add(String(bId));
                  if (ev.calendar_ids && Array.isArray(ev.calendar_ids)) {
                    ev.calendar_ids.forEach(cid => {
                      if (typeof cid === 'string' && cid.startsWith('batch_')) {
                        batchIds.add(cid.substring(6));
                      }
                    });
                  }
                  const bNameCombined = Array.from(batchIds)
                    .map(bid => batches.find(b => String(b.id) === String(bid))?.name)
                    .filter(Boolean)
                    .join(', ');

                  return (
                    <div
                      key={ev.instance_id || ev.id}
                      className={cn(
                        "group p-4 rounded-2xl border transition-all duration-200 cursor-pointer flex flex-col gap-2 relative overflow-hidden",
                        isConflict
                          ? "bg-red-500/10 border-red-500/30 hover:border-red-500/50"
                          : "bg-zinc-50 hover:bg-zinc-100 dark:bg-zinc-900/50 dark:hover:bg-zinc-800/80 border-zinc-100 dark:border-zinc-800/80 hover:border-zinc-200 dark:hover:border-zinc-700/80"
                      )}
                      style={{ opacity: ev.resolution === 'draft' ? 0.6 : 1.0 }}
                      onClick={() => {
                        handleEventClick(ev);
                      }}
                    >
                      {/* Left accent color bar */}
                      <div 
                        className="absolute left-0 top-0 bottom-0 w-1.5" 
                        style={{ backgroundColor: isConflict ? '#ef4444' : bgColor }}
                      />
                      
                      <div className="flex justify-between items-start pl-2">
                        <div className="flex flex-col gap-1 min-w-0">
                          {/* Event Title */}
                          <h4 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 truncate leading-snug">
                            {ev.title || 'Untitled'}
                          </h4>
                          
                          {/* Time */}
                          <div className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{timeStr}</span>
                            {ev.is_recurring_instance && <span className="text-[0.65rem] opacity-75">🔁</span>}
                          </div>
                        </div>
                        
                        {/* Event Type Badge */}
                        <Badge className={cn("text-[0.6rem] px-2 py-0.5 border-none font-bold uppercase tracking-wider rounded-md", 
                          ev.event_type === 'lecture' && "bg-blue-500/10 text-blue-600 dark:text-blue-400",
                          ev.event_type === 'test' && "bg-rose-500/10 text-rose-600 dark:text-rose-400",
                          ev.event_type === 'availability' && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                          ev.event_type === 'holiday' && "bg-amber-500/10 text-amber-600 dark:text-amber-400",
                          (!ev.event_type || ev.event_type === 'normal') && "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400"
                        )}>
                          {ev.event_type || 'normal'}
                        </Badge>
                      </div>

                      {/* Metadata: Faculty, Batch, Room */}
                      {(fName || bNameCombined || ev.room) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 items-center pl-2 pt-2 text-[0.7rem] text-zinc-500 dark:text-zinc-400 border-t border-zinc-100 dark:border-zinc-800/60">
                          {fName && (
                            <span className="flex items-center gap-1">
                              <span className="opacity-60">Faculty:</span>
                              <span className="font-semibold text-zinc-700 dark:text-zinc-300">{fName}</span>
                            </span>
                          )}
                          {bNameCombined && (
                            <span className="flex items-center gap-1">
                              <span className="opacity-60">Batch:</span>
                              <span className="font-semibold text-zinc-700 dark:text-zinc-300">{bNameCombined}</span>
                            </span>
                          )}
                          {ev.room && (
                            <span className="flex items-center gap-1">
                              <span className="opacity-60">Room:</span>
                              <span className="font-semibold text-zinc-700 dark:text-zinc-300">{ev.room}</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            
            {/* Footer action to create new event */}
            <div className="mt-4 pt-3 border-t border-zinc-100 dark:border-zinc-800 flex justify-end gap-2">
              <Button 
                variant="outline" 
                className="h-10 rounded-xl text-xs font-bold" 
                onClick={() => setSelectedDayForModal(null)}
              >
                Close
              </Button>
              <Button 
                className="h-10 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-lg shadow-blue-500/20" 
                onClick={() => {
                  const day = selectedDayEvents.day;
                  setSelectedDayForModal(null);
                  openNewEvent(day);
                }}
              >
                Schedule Event
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Version History & Management Modal */}
      {isVersionModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[150] p-4 animate-fade-in">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 w-full max-w-5xl shadow-2xl border border-zinc-200 dark:border-zinc-800 animate-scale-in flex flex-col max-h-[90vh]">
            
            {/* Header */}
            <div className="flex items-center justify-between mb-4 border-b border-zinc-100 dark:border-zinc-800 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-950/50 flex items-center justify-center text-violet-600 dark:text-violet-400">
                  <History className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black tracking-tighter dark:text-white uppercase">
                    Version Registry
                  </h3>
                  <p className="text-[11px] text-zinc-400 dark:text-zinc-500 font-medium">
                    Audit previous algorithm runs, restore past snapshots, or compare schedule deltas.
                  </p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setIsVersionModalOpen(false);
                  clearDiff();
                }} 
                className="w-8 h-8 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Workspace Grid */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-6">
              
              {/* Left Column: Versions List (5 cols) */}
              <div className="lg:col-span-5 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Available Snapshots</span>
                  {selectedDiffA && (
                    <button onClick={clearDiff} className="text-[10px] text-violet-500 hover:underline font-semibold">
                      Clear Selections
                    </button>
                  )}
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                  {versionHistoryLoading && versionHistory.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-zinc-400 gap-2">
                      <Loader2 className="w-6 h-6 animate-spin" />
                      <span className="text-xs">Fetching version logs...</span>
                    </div>
                  ) : versionHistory.length === 0 ? (
                    <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl">
                      <History className="w-10 h-10 text-zinc-300 dark:text-zinc-700 mx-auto mb-2" />
                      <p className="text-sm font-bold text-zinc-500">Run initialization first</p>
                      <p className="text-xs text-zinc-400 mt-1 max-w-[200px] mx-auto">No versions exist yet for this team. Generate your first schedule to start tracking.</p>
                    </div>
                  ) : (
                    versionHistory.map((ver) => {
                      const typeInfo = JOB_TYPE_LABEL[ver.job_type] || { label: ver.job_type, color: 'bg-zinc-100 text-zinc-600' };
                      const dt = ver.created_at ? new Date(ver.created_at) : null;
                      const dtStr = dt ? dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
                      
                      const isA = selectedDiffA === ver.id;
                      const isB = selectedDiffB === ver.id;
                      const isEditing = editingVersionId === ver.id;
                      const liveCount = ver.live_event_count ?? ver.events_count;

                      return (
                        <div 
                          key={ver.id}
                          onClick={() => !isEditing && selectForDiff(ver.id)}
                          className={cn(
                            "group p-3.5 rounded-2xl border text-left cursor-pointer transition-all flex flex-col gap-2 relative",
                            isA && "border-violet-500 bg-violet-50/30 dark:bg-violet-950/10 shadow-md shadow-violet-500/5",
                            isB && "border-amber-500 bg-amber-50/20 dark:bg-amber-950/5 shadow-md shadow-amber-500/5",
                            (!isA && !isB) && "border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
                          )}
                        >
                          {/* Selection indicator badges */}
                          {isA && (
                            <span className="absolute top-3 right-3 text-[8px] font-black uppercase bg-violet-500 text-white px-1.5 py-0.5 rounded-md">
                              Base (A)
                            </span>
                          )}
                          {isB && (
                            <span className="absolute top-3 right-3 text-[8px] font-black uppercase bg-amber-500 text-white px-1.5 py-0.5 rounded-md">
                              Target (B)
                            </span>
                          )}

                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-zinc-400">v{ver.version_num}</span>
                            <span className={cn('text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full', typeInfo.color)}>
                              {typeInfo.label}
                            </span>
                          </div>

                          {/* Editable or Static Label */}
                          {isEditing ? (
                            <div className="flex items-center gap-1.5 mt-0.5" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="text"
                                value={editVersionLabel}
                                onChange={(e) => setEditVersionLabel(e.target.value)}
                                className="flex-1 text-xs px-2 py-1 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-1 focus:ring-violet-500 dark:text-white"
                                placeholder="Edit label..."
                                autoFocus
                              />
                              <button 
                                onClick={() => handleSaveVersionLabel(ver.id)}
                                className="p-1 rounded-md bg-emerald-500 text-white hover:bg-emerald-600"
                              >
                                <Save className="w-3.5 h-3.5" />
                              </button>
                              <button 
                                onClick={() => setEditingVersionId(null)}
                                className="p-1 rounded-md bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-300"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs font-bold text-zinc-700 dark:text-zinc-200 pr-12 line-clamp-1">
                              {ver.label || <span className="text-zinc-300 dark:text-zinc-600 italic">No description</span>}
                            </p>
                          )}

                          {/* Version Metadata Row */}
                          <div className="flex items-center justify-between text-[10px] text-zinc-400 dark:text-zinc-500 mt-1 border-t border-zinc-100 dark:border-zinc-800/40 pt-2 font-medium">
                            <div className="flex items-center gap-3">
                              <span>{liveCount} events</span>
                              {ver.total_faculty > 0 && <span>{ver.total_faculty} faculty</span>}
                              {ver.created_by && <span className="truncate max-w-[80px]">by {ver.created_by}</span>}
                            </div>
                            <span className="shrink-0">{dtStr}</span>
                          </div>

                          {/* Quick Hover Actions (Edit/Delete) */}
                          <div 
                            className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {!isEditing && (
                              <button
                                onClick={() => {
                                  setEditingVersionId(ver.id);
                                  setEditVersionLabel(ver.label || '');
                                }}
                                className="p-1 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:shadow-sm"
                                title="Edit version label"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteVersion(ver.id)}
                              disabled={deletingVersionId === ver.id}
                              className="p-1 rounded bg-zinc-100 dark:bg-zinc-800 text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20"
                              title="Delete version"
                            >
                              {deletingVersionId === ver.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                            </button>
                          </div>

                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Right Column: Version Details / Diff Explorer (7 cols) */}
              <div className="lg:col-span-7 flex flex-col min-h-0 border-l border-zinc-100 dark:border-zinc-800 pl-6">
                
                {/* Mode 1: Selected 2 different versions -> Diff view */}
                {selectedDiffA && selectedDiffB && selectedDiffA !== selectedDiffB ? (
                  <div className="flex-1 flex flex-col min-h-0 gap-4">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Comparison Diff</span>
                        <h4 className="text-sm font-bold text-zinc-800 dark:text-zinc-200">
                          v{versionHistory.find(v => v.id === selectedDiffA)?.version_num} ↔ v{versionHistory.find(v => v.id === selectedDiffB)?.version_num}
                        </h4>
                      </div>
                      <button 
                        onClick={clearDiff} 
                        className="text-xs bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 px-3 py-1.5 rounded-xl border border-zinc-100 dark:border-zinc-800 font-bold transition-all text-zinc-500 dark:text-zinc-300"
                      >
                        Exit comparison
                      </button>
                    </div>

                    {diffLoading ? (
                      <div className="flex-1 flex flex-col items-center justify-center text-zinc-400 gap-2">
                        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                        <span className="text-xs font-semibold">Computing schedule differences...</span>
                      </div>
                    ) : diffResult ? (
                      <div className="flex-1 flex flex-col min-h-0 gap-4">
                        
                        {/* Diff stats chips */}
                        <div className="grid grid-cols-4 gap-2.5">
                          <div className="bg-emerald-500/10 dark:bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-2.5 text-center">
                            <span className="block text-lg font-black text-emerald-600 dark:text-emerald-400 leading-none mb-1">{diffResult.summary?.added || 0}</span>
                            <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Added</span>
                          </div>
                          <div className="bg-rose-500/10 dark:bg-rose-500/5 border border-rose-500/20 rounded-2xl p-2.5 text-center">
                            <span className="block text-lg font-black text-rose-600 dark:text-rose-400 leading-none mb-1">{diffResult.summary?.removed || 0}</span>
                            <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Removed</span>
                          </div>
                          <div className="bg-amber-500/10 dark:bg-amber-500/5 border border-amber-500/20 rounded-2xl p-2.5 text-center">
                            <span className="block text-lg font-black text-amber-600 dark:text-amber-400 leading-none mb-1">{diffResult.summary?.shifted || 0}</span>
                            <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Shifted</span>
                          </div>
                          <div className="bg-zinc-500/10 dark:bg-zinc-500/5 border border-zinc-500/20 rounded-2xl p-2.5 text-center">
                            <span className="block text-lg font-black text-zinc-500 dark:text-zinc-400 leading-none mb-1">{diffResult.summary?.unchanged || 0}</span>
                            <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Unchanged</span>
                          </div>
                        </div>

                        {/* List of delta modifications */}
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block">Modification Log</span>
                        <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                          {(!diffResult.added?.length && !diffResult.removed?.length && !diffResult.shifted?.length) ? (
                            <div className="text-center py-12 text-zinc-400 bg-zinc-50/40 dark:bg-zinc-950/20 border border-zinc-100 dark:border-zinc-800 rounded-2xl">
                              <CheckCircle2 className="w-8 h-8 text-emerald-500/80 mx-auto mb-2" />
                              <p className="text-xs font-bold">Identical Schedules</p>
                              <p className="text-[10px] opacity-75 mt-0.5">There are zero differences between these two versions.</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {/* Added events */}
                              {diffResult.added?.map((ev, i) => (
                                <div key={`add-${i}`} className="p-3 bg-emerald-500/10 dark:bg-emerald-500/5 border-l-4 border-emerald-500 rounded-r-xl text-left">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-black text-zinc-700 dark:text-zinc-200 truncate">{ev.title || 'Lecture'}</span>
                                    <span className="text-[8px] font-black uppercase text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded">added</span>
                                  </div>
                                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-1">
                                    {new Date(ev.start_time).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                                  </p>
                                </div>
                              ))}

                              {/* Removed events */}
                              {diffResult.removed?.map((ev, i) => (
                                <div key={`rem-${i}`} className="p-3 bg-rose-500/10 dark:bg-rose-500/5 border-l-4 border-rose-500 rounded-r-xl text-left">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-black text-zinc-700 dark:text-zinc-200 truncate">{ev.title || 'Lecture'}</span>
                                    <span className="text-[8px] font-black uppercase text-rose-600 bg-rose-500/10 px-1.5 py-0.5 rounded">removed</span>
                                  </div>
                                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-1">
                                    {new Date(ev.start_time).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                                  </p>
                                </div>
                              ))}

                              {/* Shifted events */}
                              {diffResult.shifted?.map((item, i) => (
                                <div key={`shf-${i}`} className="p-3 bg-amber-500/10 dark:bg-amber-500/5 border-l-4 border-amber-500 rounded-r-xl text-left">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-black text-zinc-700 dark:text-zinc-200 truncate">{item.event?.title || 'Lecture'}</span>
                                    <span className="text-[8px] font-black uppercase text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded">shifted</span>
                                  </div>
                                  <div className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-1 flex items-center gap-1.5 flex-wrap">
                                    <span className="line-through">{new Date(item.previous_start).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</span>
                                    <ArrowRight className="w-3 h-3 text-amber-500" />
                                    <span className="font-bold text-zinc-700 dark:text-zinc-300">{new Date(item.event?.start_time).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : selectedDiffA ? (
                  // Mode 2: Selected only one version -> View Details & Restore
                  (() => {
                    const ver = versionHistory.find(v => v.id === selectedDiffA);
                    if (!ver) return null;
                    const liveCount = ver.live_event_count ?? ver.events_count;
                    const dt = ver.created_at ? new Date(ver.created_at) : null;
                    const dtStr = dt ? dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                    
                    return (
                      <div className="flex-1 flex flex-col min-h-0 justify-between">
                        <div className="space-y-5">
                          <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Snapshot Audit</span>
                            <h4 className="text-base font-black text-zinc-800 dark:text-white uppercase mt-0.5">
                              Version {ver.version_num} Details
                            </h4>
                          </div>

                          {/* Snapshot Metrics Grid */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-100 dark:border-zinc-800/60 p-4 rounded-2xl">
                              <span className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest block mb-1">Generated Output</span>
                              <span className="text-2xl font-black text-zinc-800 dark:text-zinc-100">{liveCount}</span>
                              <span className="text-[10px] text-zinc-400 block mt-0.5 font-medium">Lectures in Database</span>
                            </div>
                            <div className="bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-100 dark:border-zinc-800/60 p-4 rounded-2xl">
                              <span className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest block mb-1">Active Faculty</span>
                              <span className="text-2xl font-black text-zinc-800 dark:text-zinc-100">{ver.total_faculty || 0}</span>
                              <span className="text-[10px] text-zinc-400 block mt-0.5 font-medium">Scheduled in Run</span>
                            </div>
                          </div>

                          {/* Algorithm Settings & Signature */}
                          <div className="bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-100 dark:border-zinc-800/60 rounded-2xl p-4 space-y-3.5">
                            <span className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest block border-b border-zinc-200/50 dark:border-zinc-800 pb-1.5">Scheduling Settings</span>
                            
                            <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-xs">
                              <div className="flex justify-between items-center">
                                <span className="text-zinc-400 font-medium">Lock Horizon</span>
                                <span className="font-semibold text-zinc-800 dark:text-zinc-200">{ver.lock_hours || 24} hours</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-zinc-400 font-medium">Rolling Horizon</span>
                                <span className="font-semibold text-zinc-800 dark:text-zinc-200">{ver.rolling_weeks || 4} weeks</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-zinc-400 font-medium">Draft Period</span>
                                <span className="font-semibold text-zinc-800 dark:text-zinc-200">{ver.draft_weeks || 2} weeks</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-zinc-400 font-medium">Run Mode</span>
                                <span className="font-bold text-violet-600 dark:text-violet-400 capitalize">{ver.job_type || 'initialize'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Run Context */}
                          <div className="text-xs space-y-2 border-t border-zinc-100 dark:border-zinc-800/60 pt-4 font-medium">
                            <div className="flex items-center justify-between">
                              <span className="text-zinc-400">Snapshot Captured</span>
                              <span className="font-semibold text-zinc-700 dark:text-zinc-300">{dtStr}</span>
                            </div>
                            {ver.created_by && (
                              <div className="flex items-center justify-between">
                                <span className="text-zinc-400">Triggered By</span>
                                <span className="font-semibold text-zinc-700 dark:text-zinc-300">{ver.created_by}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Actions (Restore) */}
                        <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800 flex flex-col gap-2.5">
                          {restoreVersionTarget?.id === ver.id ? (
                            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl text-left flex flex-col gap-2.5 animate-fade-in">
                              <div className="flex gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-xs font-bold text-amber-800 dark:text-amber-300">Destructive Calendar Overwrite</p>
                                  <p className="text-[10px] text-amber-700 dark:text-amber-400/80 mt-0.5 font-medium">
                                    Restoring will wipe all currently unlocked lecture events and replace them with this version's events. Locked events remain untouched. Current live calendar will be backed up as a new snapshot first.
                                  </p>
                                </div>
                              </div>
                              <div className="flex justify-end gap-2 text-xs">
                                <button 
                                  onClick={() => setRestoreVersionTarget(null)}
                                  className="px-3 py-1.5 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-800 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700 font-bold"
                                  disabled={restoreVersionLoading}
                                >
                                  Cancel
                                </button>
                                <button 
                                  onClick={handleRestoreVersion}
                                  className="px-4 py-1.5 rounded-xl bg-amber-600 text-white hover:bg-amber-700 font-black shadow-md shadow-amber-600/15 flex items-center gap-1.5"
                                  disabled={restoreVersionLoading}
                                >
                                  {restoreVersionLoading ? (
                                    <>
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      <span>Restoring...</span>
                                    </>
                                  ) : (
                                    <>
                                      <RotateCcw className="w-3.5 h-3.5" />
                                      <span>Confirm Restore</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button 
                                onClick={() => setRestoreVersionTarget(ver)}
                                className="flex-1 h-11 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-black shadow-lg shadow-violet-500/25 flex items-center justify-center gap-2 transition-all active:scale-98"
                              >
                                <RotateCcw className="w-4 h-4" />
                                <span>Restore version {ver.version_num} to Live Calendar</span>
                              </button>
                            </div>
                          )}
                          <p className="text-[9px] text-zinc-400 text-center font-medium">Tip: Select a second version from the list to compare differences between them.</p>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  // Mode 3: No selections yet
                  <div className="flex-1 flex flex-col items-center justify-center text-zinc-400">
                    <History className="w-12 h-12 text-zinc-300 dark:text-zinc-700 mb-3 animate-pulse" />
                    <p className="text-sm font-bold text-zinc-500">No Snapshot Selected</p>
                    <p className="text-xs text-zinc-400 mt-1 max-w-[280px] text-center font-medium">
                      Select a scheduling run from the left column to audit details, roll back calendar state, or select multiple for a delta analysis.
                    </p>
                  </div>
                )}

              </div>
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
}


function getViewRange(view, date) {
  if (view === 'day') return { start: startOfDay(date), end: endOfDay(date) };
  if (view === 'week') {
    const ws = startOfWeek(date, { weekStartsOn: 1 });
    return { start: startOfDay(ws), end: endOfDay(addDays(ws, 6)) };
  }
  if (view === 'agenda') {
    // Agenda shows 3 months of data
    return { start: startOfMonth(date), end: endOfMonth(addMonths(date, 2)) };
  }
  return { start: startOfMonth(date), end: endOfMonth(date) };
}
